<div class="p-4 dark:text-app-gray-300">
  <div class="flex items-center justify-between mb-4">
    <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Customer Directory</h2>
    <div class="flex gap-2 items-center">
      <span *ngIf="clientCountsComputedAt" class="text-xs text-gray-500 dark:text-gray-400">
        Counts as of {{ fmtDate(clientCountsComputedAt) }}
      </span>
      <button class="text-xs uppercase tracking-wide border border-amber-500 rounded px-3 py-1 hover:bg-amber-500/10 text-amber-600 dark:text-amber-400"
              (click)="refresh_client_counts()" title="Recompute user counts per customer from the Cognito user cache. Heavy — not run automatically.">
        <i class="fa-solid fa-rotate mr-1"></i> Refresh Client Counts
      </button>
      <button class="text-xs uppercase tracking-wide border border-gray-300 dark:border-[#434863] rounded px-3 py-1 hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-900 dark:text-white"
              (click)="refresh()" [disabled]="loading">
        <ng-container *ngIf="loading">Loading…</ng-container>
        <ng-container *ngIf="!loading">Refresh</ng-container>
      </button>
      <button class="text-xs uppercase tracking-wide border border-blue-600 rounded px-3 py-1 bg-blue-600 text-white hover:bg-blue-700"
              (click)="openCreate()">
        + New customer
      </button>
    </div>
  </div>

  <div *ngIf="!loading && records.length === 0" class="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
    No customers yet. Click <strong>+ New customer</strong> to add one.
  </div>

  <table *ngIf="records.length > 0" class="w-full text-sm border-collapse">
    <thead>
      <tr class="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863]">
        <th class="py-2 pr-3 w-10"></th>
        <th class="py-2 pr-3">Slug</th>
        <th class="py-2 pr-3">Name</th>
        <th class="py-2 pr-3">Status</th>
        <th class="py-2 pr-3">Scanner host</th>
        <th class="py-2 pr-3">Domains</th>
        <th class="py-2 pr-3">URLs</th>
        <th class="py-2 pr-3 text-center" title="When OFF, the f2 login page hides the 'Forgot password?' link for this customer's hostnames. Default ON.">Forgot pw</th>
        <th class="py-2 pr-3 text-center" title="Customer-wide wildcard scanner access. When active (date in the future), every user of this customer gets access to every scanner regardless of their custom:scanners. Empty = normal per-user entitlement.">Wildcard until</th>
        <th class="py-2 pr-3" title="SSO auto-create defaults (default_scanners + support_contact_html). Click to edit.">Auto-create defaults</th>
        <th class="py-2 pr-3 text-right"># Scanners</th>
        <th class="py-2 pr-3 text-right"># Clients</th>
        <th class="py-2 pr-3 text-center" title="Histogram of ENABLED client users by custom:last_login age — each user counted in exactly one bucket. Disabled users excluded.">Recent Use</th>
        <th class="py-2 pr-3">Repos</th>
        <th class="py-2 pr-3">Updated</th>
        <th class="py-2 pr-3 w-10"></th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let r of records" class="border-b border-gray-200 dark:border-[#434863] align-top">
        <td class="py-2 pr-3 whitespace-nowrap">
          <button class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mr-2"
                  (click)="openEdit(r)" title="Edit customer (full)">
            <i class="fa-solid fa-pen"></i>
          </button>
          <button class="hover:text-app-teal-100"
                  [class.text-green-500]="r.sso_portal || r.mta_endpoint_allowed || r._id === 'mta'"
                  [class.text-gray-400]="!(r.sso_portal || r.mta_endpoint_allowed || r._id === 'mta')"
                  [class.dark:text-gray-500]="!(r.sso_portal || r.mta_endpoint_allowed || r._id === 'mta')"
                  (click)="openPortalSettings(r)"
                  [title]="_portalIconTitle(r)">
            <i class="fa-solid fa-key"></i>
          </button>
          <button class="hover:text-app-teal-100 ml-2"
                  [class.text-green-500]="r.woocommerce?.enabled || r.stripe?.enabled || r.kartra?.enabled || r.samcart?.enabled || r.keap?.enabled"
                  [class.text-gray-400]="!(r.woocommerce?.enabled || r.stripe?.enabled || r.kartra?.enabled || r.samcart?.enabled || r.keap?.enabled)"
                  [class.dark:text-gray-500]="!(r.woocommerce?.enabled || r.stripe?.enabled || r.kartra?.enabled || r.samcart?.enabled || r.keap?.enabled)"
                  (click)="openCommerceSettings(r)"
                  [title]="(r.woocommerce?.enabled || r.stripe?.enabled || r.kartra?.enabled || r.samcart?.enabled || r.keap?.enabled) ? 'Payments &amp; E-Commerce (configured)' : 'Configure Payments &amp; E-Commerce'">
            <i class="fa-solid fa-dollar-sign"></i>
          </button>
        </td>
        <td class="py-2 pr-3 font-mono text-xs text-gray-900 dark:text-white">{{ r._id }}</td>
        <td class="py-2 pr-3 text-gray-900 dark:text-white">
          <ng-container *ngIf="r.name">{{ r.name }}</ng-container>
          <ng-container *ngIf="!r.name">—</ng-container>
        </td>
        <td class="py-2 pr-3 text-xs">
          <span [ngClass]="{
            'inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wide': true,
            'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300': r.status === 'active',
            'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300': r.status === 'paused',
            'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300': r.status === 'churned' || !r.status
          }">
            <ng-container *ngIf="r.status">{{ r.status }}</ng-container>
            <ng-container *ngIf="!r.status">—</ng-container>
          </span>
        </td>
        <td class="py-2 pr-3 text-xs font-mono text-gray-700 dark:text-gray-300">
          <a *ngIf="r.scanner_host"
             [href]="'https://' + r.scanner_host + '/'"
             target="_blank" rel="noopener"
             class="text-blue-600 dark:text-blue-400 hover:underline"
             [title]="'Open https://' + r.scanner_host + '/'">
            {{ r.scanner_host }}
            <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[9px] opacity-70"></i>
          </a>
          <span *ngIf="!r.scanner_host" class="text-gray-400 dark:text-gray-500">—</span>
          <i *ngIf="r.customer_owned_domain" class="fa-solid fa-building ml-1 text-[10px] text-app-teal-100"
             title="Customer-owned domain"></i>
        </td>
        <td class="py-2 pr-3 text-xs">
          <div *ngFor="let d of (r.domains || [])" class="font-mono leading-tight text-gray-700 dark:text-gray-300">{{ d }}</div>
        </td>
        <td class="py-2 pr-3 text-xs">
          <div *ngFor="let u of (r.urls || [])" class="leading-tight">
            <span class="text-gray-400 dark:text-gray-500">{{ u.label }}:</span>
            <a [href]="u.url" target="_blank" class="text-blue-600 dark:text-blue-400 hover:underline">{{ u.url }}</a>
          </div>
        </td>
        <td class="py-2 pr-3 text-xs text-center">
          <ng-container [ngSwitch]="r.auth?.forgot_password_mode || (r.auth?.forgot_password_enabled === false ? 'blocked' : 'cognito')">
            <span *ngSwitchCase="'magic_link'" class="inline-flex items-center gap-1 text-[10px] text-app-teal-100"
                  title="Forgot password sends a magic link (no 6-digit code)">
              <i class="fa-solid fa-link"></i> Magic
            </span>
            <span *ngSwitchCase="'blocked'" class="inline-flex items-center gap-1 text-[10px] text-red-500"
                  title="Forgot password is hidden for this customer">
              <i class="fa-solid fa-xmark"></i> Blocked
            </span>
            <span *ngSwitchDefault class="inline-flex items-center gap-1 text-[10px] text-green-500"
                  title="Forgot password uses Cognito's 6-digit code flow (default)">
              <i class="fa-solid fa-check"></i> Cognito
            </span>
          </ng-container>
        </td>
        <td class="py-2 pr-3 text-xs text-center whitespace-nowrap">
          <ng-container *ngIf="r.wildcard_access_until; else noWildcard">
            <span *ngIf="isWildcardActive(r.wildcard_access_until)"
                  class="inline-block px-2 py-0.5 rounded text-[10px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300"
                  [title]="'Every user of this customer gets every scanner until ' + fmtDateOnly(r.wildcard_access_until)">
              {{ fmtDateOnly(r.wildcard_access_until) }}
            </span>
            <span *ngIf="!isWildcardActive(r.wildcard_access_until)"
                  class="inline-block px-2 py-0.5 rounded text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 line-through"
                  [title]="'Expired ' + fmtDateOnly(r.wildcard_access_until) + ' — per-user entitlement now applies'">
              {{ fmtDateOnly(r.wildcard_access_until) }}
            </span>
          </ng-container>
          <ng-template #noWildcard>
            <span class="text-gray-400 dark:text-gray-500">—</span>
          </ng-template>
        </td>
        <td class="py-2 pr-3 text-xs">
          <button *ngIf="r.sso_portal; else noPortal"
                  type="button"
                  class="text-left w-full hover:text-app-teal-100 cursor-pointer leading-tight"
                  (click)="openPortalSettings(r)"
                  title="Click to edit auto-create defaults">
            <div class="font-mono text-[11px] text-gray-700 dark:text-gray-300">
              <span class="text-gray-400 dark:text-gray-500">scanners:</span>
              <span *ngIf="r.sso_portal.default_scanners?.length">{{ (r.sso_portal.default_scanners || []).join(', ') }}</span>
              <span *ngIf="!r.sso_portal.default_scanners?.length" class="text-gray-400 dark:text-gray-500">—</span>
            </div>
            <div class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
              <i *ngIf="r.sso_portal.support_contact_html" class="fa-solid fa-circle-check text-green-500 mr-1" title="Support contact HTML configured"></i>
              <i *ngIf="!r.sso_portal.support_contact_html" class="fa-regular fa-circle text-gray-400 mr-1" title="No support contact HTML"></i>
              <span>support html</span>
            </div>
          </button>
          <ng-template #noPortal>
            <span class="text-gray-400 dark:text-gray-500">—</span>
          </ng-template>
        </td>
        <td class="py-2 pr-3 text-xs text-right text-gray-900 dark:text-white font-semibold">
          <a [routerLink]="['/admin/scanners']" [queryParams]="{ client: r._id }"
             class="text-blue-600 dark:text-blue-400 hover:underline"
             title="Open the Scanners page filtered to this customer">
            {{ r.scanner_count || 0 }}
          </a>
        </td>
        <td class="py-2 pr-3 text-xs text-right text-gray-900 dark:text-white">
          <ng-container *ngIf="r.client_count">
            <a [routerLink]="['/admin/users']" [queryParams]="{ client: r._id, status: 'active' }"
               class="text-green-600 dark:text-green-400 hover:underline font-semibold"
               title="Active users with this customer slug">{{ r.client_count.active }}</a>
            <span class="text-gray-400 dark:text-gray-500 mx-1">/</span>
            <a [routerLink]="['/admin/users']" [queryParams]="{ client: r._id, status: 'inactive' }"
               class="text-red-600 dark:text-red-400 hover:underline font-semibold"
               title="Disabled users with this customer slug">{{ r.client_count.inactive }}</a>
            <span class="text-gray-400 dark:text-gray-500 mx-1">/</span>
            <a [routerLink]="['/admin/users']" [queryParams]="{ client: r._id }"
               class="text-blue-600 dark:text-blue-400 hover:underline font-semibold"
               title="All users with this customer slug">{{ r.client_count.total }}</a>
          </ng-container>
          <span *ngIf="!r.client_count"
                class="text-gray-400 dark:text-gray-500" title="Click 'Refresh Client Counts' at the top to compute">
            —
          </span>
        </td>
        <td class="py-2 pr-3 text-xs">
          <ng-container *ngIf="r.client_count?.recent">
            <div class="inline-flex items-center gap-0.5 font-mono">
              <span class="px-1 rounded text-[10px] bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-300"
                    [title]="r.client_count!.recent!.day + ' enabled user(s) signed in within the last 24 hours'">{{ r.client_count!.recent!.day }}</span>
              <span class="px-1 rounded text-[10px] bg-green-100 dark:bg-green-900/60 text-green-800 dark:text-green-300"
                    [title]="r.client_count!.recent!.week + ' enabled user(s) signed in 1–7 days ago'">{{ r.client_count!.recent!.week }}</span>
              <span class="px-1 rounded text-[10px] bg-yellow-100 dark:bg-yellow-900/60 text-yellow-800 dark:text-yellow-300"
                    [title]="r.client_count!.recent!.month + ' enabled user(s) signed in 7–30 days ago'">{{ r.client_count!.recent!.month }}</span>
              <span class="px-1 rounded text-[10px] bg-orange-100 dark:bg-orange-900/60 text-orange-800 dark:text-orange-300"
                    [title]="r.client_count!.recent!.quarter + ' enabled user(s) signed in 30–90 days ago'">{{ r.client_count!.recent!.quarter }}</span>
              <span class="px-1 rounded text-[10px] bg-red-100 dark:bg-red-900/60 text-red-800 dark:text-red-300"
                    [title]="r.client_count!.recent!.year + ' enabled user(s) signed in 90–365 days ago'">{{ r.client_count!.recent!.year }}</span>
              <span class="px-1 rounded text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    [title]="r.client_count!.recent!.older + ' enabled user(s) last seen over a year ago, or never logged in (disabled users excluded)'">{{ r.client_count!.recent!.older }}</span>
            </div>
          </ng-container>
          <span *ngIf="!r.client_count?.recent" class="text-gray-400 dark:text-gray-500">—</span>
        </td>
        <td class="py-2 pr-3 text-xs text-gray-700 dark:text-gray-300">
          <span *ngFor="let g of (r.github?.repos || []); let last = last">
            {{ g }}<span *ngIf="!last">, </span>
          </span>
        </td>
        <td class="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">{{ fmtDate(r.updated_at) }}</td>
        <td class="py-2 pr-3">
          <button class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                  (click)="askDelete(r)" title="Delete customer">
            <i class="fa-solid fa-trash"></i>
          </button>
        </td>
      </tr>
    </tbody>
  </table>
</div>

<!-- Edit/Create modal -->
<div *ngIf="showModal"
     class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
     (click)="closeModal()">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <div class="px-4 py-3 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between">
      <h3 class="font-semibold text-gray-900 dark:text-white">
        <ng-container *ngIf="isCreate">New customer</ng-container>
        <ng-container *ngIf="!isCreate">Edit customer</ng-container>
      </h3>
      <button class="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white" (click)="closeModal()">✕</button>
    </div>
    <div class="p-4 space-y-3 text-sm">
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Slug (_id) — Cognito <code class="text-gray-700 dark:text-gray-300">custom:customers</code> + F2-ADMIN.Scanners.Client</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 disabled:bg-gray-100 disabled:dark:bg-app-black-500"
               [(ngModel)]="editing._id"
               [disabled]="!isCreate"
               placeholder="e.g. option-pit" />
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Display name</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
               [(ngModel)]="editing.name"
               placeholder="e.g. Option Pit" />
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Email signature name</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
               [(ngModel)]="editing.email_signature_name"
               placeholder="e.g. Hima" />
        <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Optional. Used as the <code class="text-gray-700 dark:text-gray-300">Thanks!<br>&lt;name&gt;</code> signoff on commerce welcome emails. Leave blank to sign as the full Display name.</p>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Status</label>
          <select class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1" [(ngModel)]="editing.status">
            <option value="active">active</option>
            <option value="paused">paused</option>
            <option value="churned">churned</option>
          </select>
        </div>
        <div class="flex items-end pb-1">
          <label class="text-sm flex items-center gap-2 text-gray-900 dark:text-white">
            <input type="checkbox" [(ngModel)]="editing.customer_owned_domain" />
            Customer-owned domain
          </label>
        </div>
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
          Scanner host — canonical hostname this customer's scanners redirect to
        </label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs"
               [(ngModel)]="editing.scanner_host"
               placeholder="ai.optionpit.com" />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Replaces the *_SCANNER_HOST env vars in f2-admin-service.
          Cached in-process for 60s; admin edits invalidate the cache immediately.
          Empty → falls back to env var, then legacy default.
        </p>
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
          Support email — surfaced to end users on login pages
        </label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs"
               type="email"
               [(ngModel)]="editing.support_email"
               placeholder="support@customer.com" />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Shown as a mailto link on the members login page and appended
          to the account-disabled toast (so users click once to draft a
          pre-filled email). Empty → nothing rendered. Distinct from
          the internal contacts roster below.
        </p>
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
          Documentation URL — top-level integration-guide pointer
        </label>
        <div class="flex gap-1.5 items-stretch">
          <input class="flex-1 border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs"
                 [(ngModel)]="editing.docs_url"
                 placeholder="/rest/admin/customer-assets/{{ editing._id }}/<asset_id>  or  https://…/guide.html" />
          <a *ngIf="editing.docs_url"
             [href]="customerDocsUrlPreviewHref()"
             target="_blank" rel="noopener"
             class="dark-btn dark-btn-sm whitespace-nowrap"
             title="Open the linked documentation in a new tab.">
            <i class="fa-solid fa-arrow-up-right-from-square mr-1"></i>Open
          </a>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Customer-level integration-guide URL — independent of <code>sso_portal.docs_url</code>.
          Use this for federated customers (e.g. <code>trend-labs</code> riding the shared MTA SSO endpoint)
          that don't carry their own <code>sso_portal</code>. Paste the served path of an asset uploaded
          below, or an absolute external URL. Leading-slash paths get
          <code>env.apiUrl</code> prepended on the Open preview.
        </p>
      </div>
      <!-- Customer assets — SharePoint-style attachment list scoped to this
           customer slug. Always reachable (independent of sso_portal config)
           so federated customers can still attach + manage docs. Reads/writes
           against the same /rest/admin/customer-assets endpoints the Portal
           SSO popup uses; that more-detailed surface stays for share-links +
           audit + visibility editing. -->
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
          <i class="fa-solid fa-paperclip mr-1"></i>Customer assets — files attached to this customer slug
        </label>
        <div class="border border-gray-300 dark:border-[#434863] rounded bg-white dark:bg-app-black-100 p-2 space-y-1">
          <div *ngIf="customerAssetsLoading" class="text-xs text-gray-500 dark:text-gray-400 px-1 py-0.5">Loading…</div>
          <div *ngIf="!customerAssetsLoading && customerAssets.length === 0" class="text-xs text-gray-500 dark:text-gray-400 italic px-1 py-0.5">
            No assets attached. Use Add below to upload an HTML/PDF/MD doc.
          </div>
          <div *ngFor="let a of customerAssets"
               class="flex items-center gap-2 px-2 py-1.5 rounded border border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-200/50">
            <i class="fa-solid mr-1 text-gray-500 dark:text-gray-400"
               [class.fa-globe]="a.content_type === 'text/html'"
               [class.fa-file-pdf]="a.content_type === 'application/pdf'"
               [class.fa-file-lines]="a.content_type === 'text/markdown' || a.filename.endsWith('.md')"
               [class.fa-file]="!['text/html','application/pdf','text/markdown'].includes(a.content_type) && !a.filename.endsWith('.md')"></i>
            <div class="flex-1 min-w-0">
              <div class="text-xs font-semibold text-gray-900 dark:text-white truncate"
                   [title]="a.label || a.filename">{{ a.label || a.filename }}</div>
              <div class="text-[10px] font-mono text-gray-500 dark:text-gray-400 truncate"
                   [title]="a.filename + ' · ' + (a.size | number) + ' bytes'">{{ a.filename }}</div>
            </div>
            <span class="flex items-center gap-1 mr-1"
                  title="Role visibility — only these roles can fetch this asset (admin always can; visibility list gates client_admin / member)">
              <span *ngFor="let r of a.visibility"
                    class="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wide"
                    [class.bg-purple-100]="r === 'admin'"
                    [class.dark:bg-purple-900]="r === 'admin'"
                    [class.text-purple-800]="r === 'admin'"
                    [class.dark:text-purple-200]="r === 'admin'"
                    [class.bg-blue-100]="r === 'client_admin'"
                    [class.dark:bg-blue-900]="r === 'client_admin'"
                    [class.text-blue-800]="r === 'client_admin'"
                    [class.dark:text-blue-200]="r === 'client_admin'"
                    [class.bg-green-100]="r === 'member'"
                    [class.dark:bg-green-900]="r === 'member'"
                    [class.text-green-800]="r === 'member'"
                    [class.dark:text-green-200]="r === 'member'">{{ r }}</span>
              <span *ngIf="!a.visibility?.length"
                    class="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wide bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    title="No roles configured — only admin can fetch.">admin-only</span>
            </span>
            <a [href]="customerAssetServeUrl(a)" target="_blank" rel="noopener"
               class="text-app-teal-100 hover:text-app-teal-200" title="Open in new tab">
              <i class="fa-solid fa-arrow-up-right-from-square"></i>
            </a>
            <button type="button"
                    class="text-app-teal-100 hover:text-app-teal-200"
                    (click)="copyCustomerAssetUrl(a)"
                    title="Copy the served URL to clipboard">
              <i class="fa-solid fa-link"></i>
            </button>
            <button type="button"
                    class="text-red-500 hover:text-red-600"
                    (click)="deleteCustomerAsset(a)"
                    title="Delete this asset (writes an audit entry)">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <div class="pt-1 mt-1 border-t border-gray-200 dark:border-[#434863]">
            <input #customerAssetFileInput type="file"
                   accept=".html,.htm,.pdf,.md,.markdown,.txt"
                   class="hidden"
                   (change)="onCustomerAssetFilePicked($event)" />
            <button type="button"
                    class="dark-btn dark-btn-sm"
                    (click)="customerAssetFileInput.click()"
                    [disabled]="customerAssetUploading">
              <i class="fa-solid fa-plus mr-1"></i>{{ customerAssetUploading ? 'Uploading…' : 'Add asset' }}
            </button>
            <span *ngIf="customerAssetUploadFile" class="text-xs text-gray-500 dark:text-gray-400 ml-2">
              {{ customerAssetUploadFile.name }} ({{ customerAssetUploadFile.size | number }} bytes) →
              <button type="button" class="text-app-teal-100 hover:underline" (click)="uploadCustomerAsset()">Upload</button>
              <button type="button" class="text-gray-500 hover:underline ml-2" (click)="customerAssetUploadFile = null; customerAssetFileInput.value = ''">Cancel</button>
            </span>
            <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
              Uploaded with default visibility <code>[admin, client_admin, member]</code>. For per-role gating
              or share-link generation, use the Configure Portal SSO popup's Integration documents section instead.
              700KB binary cap.
            </p>
          </div>
        </div>
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
          Domains — reverse-lookup key for "which customer owns this request?"
        </label>
        <div class="flex flex-wrap gap-1 p-1.5 border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 rounded min-h-[2.25rem]">
          <span *ngFor="let d of editing.domains || []"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-blue-100 dark:bg-app-blue-100/50 text-blue-900 dark:text-blue-200">
            {{ d }}
            <button type="button"
                    class="text-blue-700 dark:text-blue-300 hover:text-red-600 dark:hover:text-red-400 font-bold"
                    (click)="removeDomain(d)">×</button>
          </span>
          <input type="text"
                 class="flex-1 min-w-[8rem] bg-transparent text-xs font-mono outline-none text-gray-900 dark:text-white"
                 [(ngModel)]="domainInput"
                 (keydown.enter)="$event.preventDefault(); addDomain()"
                 (keydown.backspace)="onDomainBackspace($event)"
                 (blur)="addDomain()"
                 placeholder="add hostname + Enter" />
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Stored lowercased + deduped on save. Usually a superset that includes scanner_host.
        </p>
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">GitHub org</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
               [(ngModel)]="editing.github.org"
               placeholder="F2Tech-WebDev-v2" />
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">GitHub repos (comma-separated, repo names only — no org prefix)</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
               [(ngModel)]="reposText"
               placeholder="option-pit, option-pit-service" />
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Vercel projects (comma-separated)</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
               [(ngModel)]="vercelText"
               placeholder="option-pit" />
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Scanner _ids (comma-separated; match F2-ADMIN.Scanners._id)</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
               [(ngModel)]="scannerIdsText"
               placeholder="option-pit, op-fingerprints, op-delta-strike, op-mti" />
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">URLs (JSON array of <code>&#123; "label": "...", "url": "..." &#125;</code>)</label>
        <textarea class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs" rows="4"
                  [(ngModel)]="urlsJsonText"></textarea>
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Contacts (JSON array of <code>&#123; "role": "...", "name": "...", "email": "..." &#125;</code>)</label>
        <textarea class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs" rows="4"
                  [(ngModel)]="contactsJsonText"></textarea>
      </div>
      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Notes</label>
        <textarea class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1" rows="3"
                  [(ngModel)]="editing.notes"></textarea>
      </div>

      <!-- Wildcard scanner access — customer-wide override that bypasses
           every user's custom:scanners while the expiration date is in
           the future. Empty / past date = normal per-user entitlement. -->
      <div *ngIf="editing._id !== 'f2'"
           class="border-t border-gray-200 dark:border-[#434863] pt-3 mt-3">
        <div class="font-semibold text-sm text-gray-900 dark:text-white mb-1">Wildcard scanner access</div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Customer-wide override. While the date below is in the future, every user under this customer
          gets access to every scanner regardless of their <code>custom:scanners</code>. Checked BEFORE
          per-user entitlement. Empty or past = wildcard off, normal entitlement applies.
        </p>
        <div class="flex items-center gap-2">
          <input type="date"
                 class="border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm"
                 [value]="wildcardInputValue()"
                 (change)="setWildcardFromInput($any($event.target).value)" />
          <button type="button"
                  class="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600"
                  (click)="editing.wildcard_access_until = null"
                  *ngIf="editing.wildcard_access_until">
            Clear
          </button>
          <span *ngIf="editing.wildcard_access_until && isWildcardActive(editing.wildcard_access_until)"
                class="text-xs text-emerald-700 dark:text-emerald-400">
            Active &mdash; all users get every scanner until end of {{ fmtDateOnly(editing.wildcard_access_until) }} ET
          </span>
          <span *ngIf="editing.wildcard_access_until && !isWildcardActive(editing.wildcard_access_until)"
                class="text-xs text-amber-700 dark:text-amber-400">
            Expired &mdash; per-user entitlement now applies
          </span>
        </div>
      </div>

      <!-- Data-tier override — F2 row holds the COMPANY-WIDE setting
           (highest priority, applies to every SPA across every customer).
           All other customer rows hold a PER-CUSTOMER setting (overridden
           by company-wide, overrides per-user). Both use the same
           data_tier_override field on the Customer doc; the backend
           getBrandConfig surfaces them as company_override vs
           customer_override based on which doc resolved the host. -->
      <div class="border-t border-gray-200 dark:border-[#434863] pt-3 mt-3">
        <div class="font-semibold text-sm text-gray-900 dark:text-white mb-1">
          <span *ngIf="editing._id === 'f2'">Company-wide data-tier override</span>
          <span *ngIf="editing._id !== 'f2'">Customer data-tier override</span>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2" *ngIf="editing._id === 'f2'">
          Org-wide kill / mode switch. When set to anything but Off, every consumer SPA's
          agreements popup is suppressed (brand-config returns <code>enforced:false</code>)
          and SPAs render the chosen data tier. Per-customer settings (overrides + agreements)
          still exist but don't take effect while a company-wide override is active.
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2" *ngIf="editing._id !== 'f2'">
          Per-customer override — applies to every user of this customer's SPAs. When set to
          anything but Off, the agreements popup is suppressed for this customer and SPAs
          render the chosen data tier. The library's <code>resolveDataTier()</code> priority is:
          company-wide override &gt; this customer override &gt; per-user entitlement.
        </p>
        <div *ngIf="editing._id !== 'f2' && companyOverrideMode" class="text-xs text-amber-700 dark:text-amber-400 mb-2 italic">
          Company-wide override is active (<b>{{ companyOverrideMode }}</b>) — this customer setting
          is stored but bypassed at runtime until the company-wide override returns to Off.
        </div>
        <div class="space-y-1 pl-4 border-l-2 border-amber-300 dark:border-amber-700"
             [class.opacity-50]="editing._id !== 'f2' && companyOverrideMode">
          <label class="text-sm flex items-center gap-2 text-gray-900 dark:text-white">
            <input type="radio" name="data_tier_override"
                   [checked]="editing.data_tier_override.mode === 'off'"
                   (change)="editing.data_tier_override.mode = 'off'"
                   [disabled]="editing._id !== 'f2' && !!companyOverrideMode" />
            <span><b>Off</b> — no override; falls through to lower-priority layers.</span>
          </label>
          <label class="text-sm flex items-center gap-2 text-gray-900 dark:text-white">
            <input type="radio" name="data_tier_override"
                   [checked]="editing.data_tier_override.mode === 'realtime'"
                   (change)="editing.data_tier_override.mode = 'realtime'"
                   [disabled]="editing._id !== 'f2' && !!companyOverrideMode" />
            <span><b>Realtime</b> — SPAs serve live data, popup suppressed.</span>
          </label>
          <label class="text-sm flex items-center gap-2 text-gray-900 dark:text-white">
            <input type="radio" name="data_tier_override"
                   [checked]="editing.data_tier_override.mode === 'delayed'"
                   (change)="editing.data_tier_override.mode = 'delayed'"
                   [disabled]="editing._id !== 'f2' && !!companyOverrideMode" />
            <span><b>Delayed</b> — SPAs serve delayed data, popup suppressed.</span>
          </label>
          <label class="text-sm flex items-center gap-2 text-gray-900 dark:text-white">
            <input type="radio" name="data_tier_override"
                   [checked]="editing.data_tier_override.mode === 'disabled'"
                   (change)="editing.data_tier_override.mode = 'disabled'"
                   [disabled]="editing._id !== 'f2' && !!companyOverrideMode" />
            <span><b>Disabled</b> — SPAs render a service-unavailable state, popup suppressed.</span>
          </label>
        </div>
      </div>

      <!-- Per-customer error-report alert sound. Plays in f2-admin and
           f2-clients /admin/error-reports live grid on each new SSE event.
           Different customers can pick different notes/instruments so a
           listen tells operators WHICH customer is throwing errors.
           Surfaced via /rest/api/brand-config.error_alert_sound. -->
      <div class="border-t border-gray-200 dark:border-[#434863] pt-3 mt-3">
        <div class="font-semibold text-sm text-gray-900 dark:text-white mb-1">Error report alert sound</div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Played on each new live error report for this customer. Off by default.
        </p>
        <label class="text-sm flex items-center gap-2 text-gray-900 dark:text-white mb-2">
          <input type="checkbox"
                 [checked]="editing.error_alert_sound?.enabled === true"
                 (change)="_ensure_error_alert_sound(); editing.error_alert_sound!.enabled = $any($event.target).checked" />
          <span>Enable sound on new error reports for this customer</span>
        </label>
        <div class="grid grid-cols-2 gap-3 max-w-md">
          <label class="text-xs text-gray-700 dark:text-gray-300 flex flex-col gap-1">
            Instrument
            <select [(ngModel)]="editing.error_alert_sound!.instrument"
                    (change)="_ensure_error_alert_sound()"
                    class="bg-white border border-gray-300 text-gray-900 rounded px-2 py-1 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white">
              <option value="acoustic_grand_piano">Piano</option>
              <option value="tubular_bells">Tubular bells</option>
              <option value="marimba">Marimba</option>
              <option value="xylophone">Xylophone</option>
              <option value="glockenspiel">Glockenspiel</option>
              <option value="vibraphone">Vibraphone</option>
              <option value="celesta">Celesta</option>
              <option value="music_box">Music box</option>
              <option value="kalimba">Kalimba</option>
              <option value="steel_drums">Steel drums</option>
              <option value="church_organ">Church organ</option>
              <option value="harpsichord">Harpsichord</option>
            </select>
          </label>
          <label class="text-xs text-gray-700 dark:text-gray-300 flex flex-col gap-1">
            Note
            <select [(ngModel)]="editing.error_alert_sound!.note"
                    (change)="_ensure_error_alert_sound()"
                    class="bg-white border border-gray-300 text-gray-900 rounded px-2 py-1 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white">
              <option *ngFor="let n of soundNotes" [value]="n">{{n}}</option>
            </select>
          </label>
        </div>
        <button type="button"
                class="mt-2 text-xs px-3 py-1 rounded border border-gray-300 dark:border-[#434863] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-app-black-100"
                (click)="previewAlertSound()">
          <i class="fa-solid fa-volume-high mr-1"></i> Preview
        </button>
      </div>

      <!-- Exchange agreements gating — consumed by f2tech-shared's
           agreementsConfig() in every scanner SPA. Surfaced via
           /rest/api/brand-config so SPAs gate their popup without code edits.
           Missing on a Customer doc defaults to {enforced:true, required:true}
           on the backend (fail-safe). -->
      <div class="border-t border-gray-200 dark:border-[#434863] pt-3 mt-3">
        <div class="font-semibold text-sm text-gray-900 dark:text-white mb-1">Exchange agreements</div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Per-customer gating for the agreements popup. Customer SPAs read this via
          <code>agreementsConfig()</code> from <code>f2tech-shared</code> (cross-origin GET to
          <code>/rest/api/brand-config</code>, 60s cached).
        </p>
        <div *ngIf="companyOverrideMode" class="text-xs text-amber-700 dark:text-amber-400 mb-2 italic">
          Company-wide override is active (<b>{{ companyOverrideMode }}</b>) — these per-customer
          settings are stored but bypassed at runtime until the company-wide override returns to Off.
        </div>
        <div *ngIf="!companyOverrideMode && editingCustomerOverrideMode && editing._id !== 'f2'"
             class="text-xs text-amber-700 dark:text-amber-400 mb-2 italic">
          This customer's data-tier override is active (<b>{{ editingCustomerOverrideMode }}</b>) —
          agreements popup is suppressed for this customer regardless of these settings.
        </div>
        <div class="space-y-1 pl-4 border-l-2 border-emerald-200 dark:border-emerald-800"
             [class.opacity-50]="!!companyOverrideMode || (editing._id !== 'f2' && !!editingCustomerOverrideMode)">
          <label class="text-sm flex items-center gap-2 text-gray-900 dark:text-white">
            <input type="checkbox"
                   [checked]="editing.exchange_agreements.enforced"
                   (change)="editing.exchange_agreements.enforced = $any($event.target).checked"
                   [disabled]="!!companyOverrideMode || (editing._id !== 'f2' && !!editingCustomerOverrideMode)" />
            <span><b>Enforced</b> — when off, the SPA skips the popup entirely and never gates the scanner.</span>
          </label>
          <label class="text-sm flex items-center gap-2 text-gray-900 dark:text-white">
            <input type="checkbox"
                   [checked]="editing.exchange_agreements.required"
                   (change)="editing.exchange_agreements.required = $any($event.target).checked"
                   [disabled]="!!companyOverrideMode || (editing._id !== 'f2' && !!editingCustomerOverrideMode) || !editing.exchange_agreements.enforced" />
            <span><b>Required</b> — when on (and enforced), the user MUST complete agreements before scanner access. When off, the popup is shown informationally only.</span>
          </label>
        </div>
      </div>

      <!-- Auth flow toggles surfaced via brand-config. Today this is
           just the "Forgot password" mode; future fields live in the
           same `auth` sub-object on the Customer doc. -->
      <div class="border-t border-gray-200 dark:border-[#434863] pt-4">
        <label class="text-sm font-semibold mb-1 block text-gray-900 dark:text-white">Auth flow</label>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Per-customer settings consumed by the f2 login page. Read from the customer doc's
          <code>auth</code> field via <code>/rest/api/brand-config</code>.
        </p>
        <label class="block text-sm text-gray-900 dark:text-white">
          <span class="block mb-1"><b>Forgot password mode</b></span>
          <!-- IT-F2-407 (Mike 2026-09-18 3rd try): dropdown reported still
               empty. Root cause suspected: missing `dark:text-white` on the
               <select> meant option text inherited browser-default color,
               which in dark mode renders dark-on-dark → invisible options.
               Matches the `editing.status` select nearby which uses the
               same working pattern (bg + border + text-gray-900 dark:text-white).
               Two-way [(ngModel)] mirrors the other working selects in
               this file; openEdit() default-fills forgot_password_mode. -->
          <select class="bg-white dark:bg-app-black-100 dark:border-[#434863] border border-gray-300 text-gray-900 dark:text-white rounded-md p-2 text-sm w-full"
                  [ngModel]="forgotPasswordModeValue"
                  (ngModelChange)="setForgotPasswordMode($event)">
            <option value="cognito">Cognito — Send a 6-digit code, user enters code + new password (default)</option>
            <option value="magic_link">Magic Link — Send a one-time link, user clicks → sets new password (requires SES-wired pool)</option>
            <option value="blocked">Blocked — Hide the “Forgot password?” link entirely</option>
          </select>
        </label>

        <label class="block text-sm text-gray-900 dark:text-white mt-3">
          <span class="block mb-1"><b>External login URL</b> <span class="text-gray-500 dark:text-gray-400 font-normal">(SSO-only customers)</span></span>
          <input type="url"
                 placeholder="e.g. https://theotradelive.com/login"
                 class="bg-gray-50 dark:bg-gray-700 dark:border-gray-600 border border-gray-300 rounded-md p-2 text-sm w-full font-mono"
                 [ngModel]="editing.auth?.external_login_url || ''"
                 (ngModelChange)="editing.auth = editing.auth || {}; editing.auth.external_login_url = ($event || '').trim()" />
          <span class="block text-xs text-gray-500 dark:text-gray-400 mt-1">
            Customer's own login page. When set, this customer's per-product SPAs hard-nav here on any login-required event (canActivate fail, token expiry, Sign Out, stale bookmark). Empty → SPA uses the F2 login flow.
          </span>
        </label>

        <!-- IT-F2-392 clar b6d2815d (Mike picked option A 2026-10-19) —
             Three sender fields on Customers.<slug>.email_sender:
               support_from        → welcome / notification emails    (e.g. info@customer.com)
               transactional_from  → magic-link / forgot-password /    (e.g. noreply@customer.com)
                                     invite emails
               from_address        → fallback used when a per-type
                                     field is empty (legacy slot;
                                     kept for back-compat)
             Each distinct address needs its own SES verified sender
             identity. Backend read path (customer-directory.service.ts:2462)
             surfaces whichever applies for the current email type as
             auth.email_sender_from in /api/brand-config. -->
        <div class="mt-3 border-t border-gray-200 dark:border-[#434863] pt-3">
          <label class="block text-sm font-semibold text-gray-900 dark:text-white mb-1">Transactional email senders</label>
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Per-type "From" addresses on outgoing emails. SES must have each distinct address verified as a sender identity (configuration-set is set up out-of-band). Both per-type slots fall through to the legacy single sender when blank.
          </p>

          <label class="block text-sm text-gray-900 dark:text-white mt-2">
            <span class="block mb-1"><b>Support / welcome sender</b> <span class="text-gray-500 dark:text-gray-400 font-normal">— welcome, notifications</span></span>
            <input type="email"
                   placeholder="e.g. info&#64;customer-domain.com"
                   class="bg-gray-50 dark:bg-gray-700 dark:border-gray-600 border border-gray-300 rounded-md p-2 text-sm w-full font-mono"
                   [ngModel]="editing.email_sender?.support_from || ''"
                   (ngModelChange)="editing.email_sender = editing.email_sender || {}; editing.email_sender.support_from = ($event || '').trim()" />
            <span class="block text-xs text-gray-500 dark:text-gray-400 mt-1">
              Used as the "From" header on welcome + general notification emails. Convention: <code>info&#64;customer.com</code>.
            </span>
          </label>

          <label class="block text-sm text-gray-900 dark:text-white mt-3">
            <span class="block mb-1"><b>Transactional sender</b> <span class="text-gray-500 dark:text-gray-400 font-normal">— magic-link, forgot-password, invite</span></span>
            <input type="email"
                   placeholder="e.g. noreply&#64;customer-domain.com"
                   class="bg-gray-50 dark:bg-gray-700 dark:border-gray-600 border border-gray-300 rounded-md p-2 text-sm w-full font-mono"
                   [ngModel]="editing.email_sender?.transactional_from || ''"
                   (ngModelChange)="editing.email_sender = editing.email_sender || {}; editing.email_sender.transactional_from = ($event || '').trim()" />
            <span class="block text-xs text-gray-500 dark:text-gray-400 mt-1">
              Used as the "From" header on magic-link, forgot-password + invite emails. Convention: <code>noreply&#64;customer.com</code>.
            </span>
          </label>

          <label class="block text-sm text-gray-900 dark:text-white mt-3">
            <span class="block mb-1"><b>Legacy single sender (fallback)</b></span>
            <input type="email"
                   placeholder="e.g. noreply&#64;customer-domain.com"
                   class="bg-gray-50 dark:bg-gray-700 dark:border-gray-600 border border-gray-300 rounded-md p-2 text-sm w-full font-mono"
                   [ngModel]="editing.email_sender?.from_address || ''"
                   (ngModelChange)="editing.email_sender = editing.email_sender || {}; editing.email_sender.from_address = ($event || '').trim()" />
            <span class="block text-xs text-gray-500 dark:text-gray-400 mt-1">
              Picker falls through to this address when the per-type slot above is blank. Pre-split customers keep working off this field alone; once you populate the per-type slots this slot can stay as a safety net.
            </span>
          </label>
        </div>

        <!-- IT-F2-391 Mike c/063a66a3 2026-10-23 — audit retention knob.
             Days to keep NC alert-send audit rows on F2:NC_AUDIT.<slug>.
             Blank / 0 → service default (90). -->
        <div class="mt-3 border-t border-gray-200 dark:border-[#434863] pt-3">
          <label class="block text-sm text-gray-900 dark:text-white">
            <span class="block mb-1"><b>Audit retention (days)</b> <span class="text-gray-500 dark:text-gray-400 font-normal">— NC alert-send audit log</span></span>
            <input type="number" min="0" step="1" placeholder="90"
                   class="bg-gray-50 dark:bg-gray-700 dark:border-gray-600 border border-gray-300 rounded-md p-2 text-sm w-32 font-mono"
                   [ngModel]="editing.audit_retention_days ?? ''"
                   (ngModelChange)="editing.audit_retention_days = ($event === '' || $event === null) ? undefined : +$event" />
            <span class="block text-xs text-gray-500 dark:text-gray-400 mt-1">
              Days the notification-center keeps sent-alert audit rows for this customer before purge. Blank or <code>0</code> &rarr; service default (90 days).
            </span>
          </label>
        </div>
      </div>

      <!-- Per-customer dynamic-CSS branding for f2-members auth surfaces.
           One checkbox here (operator on/off toggle); the rest of the
           subdoc (accent_color, logo_asset_id, etc.) is provisioned
           out-of-band when onboarding a customer's brand. When unchecked,
           brand-config returns `branding: null` and f2-members renders
           the generic look. -->
      <div *ngIf="editing.branding" class="border-t border-gray-200 dark:border-[#434863] pt-4">
        <label class="text-sm font-semibold mb-1 block text-gray-900 dark:text-white">Customer CSS / branding</label>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Applies the customer's logo + accent + background to f2-members
          auth surfaces (login, forgot-password, accept-invite, set-new-
          password). Logo + colors come from the <code>branding</code>
          subdoc on the Customer record. Toggle off to fall back to the
          generic look without losing the data.
        </p>
        <label class="inline-flex items-center gap-2 text-sm text-gray-900 dark:text-white cursor-pointer">
          <input type="checkbox"
                 [ngModel]="editing.branding?.enabled === true"
                 (ngModelChange)="editing.branding = editing.branding || {}; editing.branding.enabled = $event === true"
                 class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm" />
          <span>Apply customer CSS on auth pages</span>
        </label>
        <p *ngIf="editing.branding?.accent_color || editing.branding?.background_color" class="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
          <span *ngIf="editing.branding?.accent_color">
            Accent <code [style.color]="editing.branding?.accent_color">{{ editing.branding?.accent_color }}</code>
          </span>
          <span *ngIf="editing.branding?.accent_color && editing.branding?.background_color">·</span>
          <span *ngIf="editing.branding?.background_color">
            Background <code>{{ editing.branding?.background_color }}</code>
          </span>
        </p>

        <!-- Logo asset picker. The asset itself lives in F2-ADMIN.CustomerAssets
             (upload via the standalone /admin/customer-assets panel). Save the
             asset_id here; brand-config resolves the bytes into logo_data_url
             every request via _resolveBrandingPayload (60s cache). Kept as a
             plain input rather than an inline uploader so the asset lifecycle
             (labels, versioning, share tokens, audit) stays in one place. -->
        <div class="mt-3">
          <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Logo asset id <span class="font-normal text-gray-500 dark:text-gray-400">(icon-style, square)</span>
            <span class="font-normal text-gray-500 dark:text-gray-400">
              — upload the PNG at
              <a routerLink="/admin/customer-assets" class="text-app-teal-100 hover:underline">/admin/customer-assets</a>
              first, then paste the asset id here.
            </span>
          </label>
          <input type="text"
                 [ngModel]="editing.branding?.logo_asset_id || ''"
                 (ngModelChange)="editing.branding = editing.branding || {}; editing.branding.logo_asset_id = ($event || '').trim() || null"
                 placeholder="e.g. 6892fc41ab3d9e00113a8ab5"
                 class="w-full px-2 py-1 text-xs font-mono bg-white dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded-sm text-gray-900 dark:text-white" />
          <p *ngIf="editing.branding?.logo_asset_id" class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            <i class="fa-solid fa-image mr-1"></i>
            Referenced asset: <code>{{ editing.branding?.logo_asset_id }}</code>
          </p>
        </div>

        <div class="mt-3">
          <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Wordmark asset id <span class="font-normal text-gray-500 dark:text-gray-400">(text-logo, wide)</span>
            <span class="font-normal text-gray-500 dark:text-gray-400">
              — companion to the icon. Renders side-by-side on auth
              surfaces + emails when both are set, matching the
              customer's own site header treatment.
            </span>
          </label>
          <input type="text"
                 [ngModel]="editing.branding?.wordmark_asset_id || ''"
                 (ngModelChange)="editing.branding = editing.branding || {}; editing.branding.wordmark_asset_id = ($event || '').trim() || null"
                 placeholder="e.g. 6a46d001c2d1b7e08d5f4c02"
                 class="w-full px-2 py-1 text-xs font-mono bg-white dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded-sm text-gray-900 dark:text-white" />
          <p *ngIf="editing.branding?.wordmark_asset_id" class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            <i class="fa-solid fa-font mr-1"></i>
            Referenced asset: <code>{{ editing.branding?.wordmark_asset_id }}</code>
          </p>
        </div>
      </div>

      <!-- SSO portal-handoff config — full editor lives in the Portal SSO
           Settings popup (open via the key icon on the customer row in
           the directory table). This main editor intentionally does NOT
           duplicate those fields to avoid two save paths landing in the
           same sso_portal subtree. -->
      <div *ngIf="!isCreate" class="border-t border-gray-200 dark:border-[#434863] pt-3 mt-3">
        <div class="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <i class="fa-solid fa-key text-app-teal-100"></i>
          <span>
            SSO portal handoff config &mdash; <strong>edit via the key icon</strong>
            on the customer row in the directory table
            (Portal SSO Settings popup). Toggle, secrets, handoff mode,
            identity mapping, claims, perms schema all live there.
          </span>
        </div>
        <p *ngIf="editing.sso_portal" class="text-xs text-green-600 dark:text-green-400 mt-1">
          <i class="fa-solid fa-circle-check mr-1"></i>SSO portal is currently configured on this customer.
        </p>
        <p *ngIf="!editing.sso_portal" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          <i class="fa-regular fa-circle mr-1"></i>No SSO portal configured. Open the Portal SSO Settings popup to enable.
        </p>
      </div>
    </div>

    <div class="px-4 py-3 border-t border-gray-200 dark:border-[#434863] flex justify-end gap-2">
      <button class="px-3 py-1 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-900 dark:text-white" (click)="closeModal()">Cancel</button>
      <!-- IT-F2-407 (Mike 2026-09-18 4th try): even the two *ngIf buttons
           still rendered empty in Mike's screenshot. Give up on conditional
           rendering entirely — one unconditional Save button, matching the
           minimal pattern from users.component Add User drawer. Backend
           handles both create + edit paths from saveModal(); the label
           difference was cosmetic. -->
      <button type="button" class="dark-btn-primary px-3 py-1" (click)="saveModal()">Save</button>
    </div>
  </div>
</div>

<!-- One-time secret reveal — sits on top of the edit modal (z-60 > z-50) -->
<div *ngIf="revealedSecret"
     class="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-xl max-w-lg w-full border-2 border-amber-500 p-4"
       (click)="$event.stopPropagation()">
    <h3 class="text-base font-bold text-amber-600 dark:text-amber-400 mb-2">
      <i class="fa-solid fa-triangle-exclamation mr-1"></i>New secret — shown ONCE
    </h3>
    <p class="text-xs text-gray-700 dark:text-gray-300 mb-2">
      Copy this now and share with the customer via a one-time-secret link
      (<a href="https://us.onetimesecret.com/" target="_blank" rel="noopener" class="underline">onetimesecret.com</a>).
      Once you dismiss this dialog the plaintext cannot be retrieved again — only the fingerprint.
    </p>
    <pre class="bg-gray-100 dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded p-2 font-mono text-xs break-all text-gray-900 dark:text-white">{{ revealedSecret.secret }}</pre>
    <div class="text-xs text-gray-500 dark:text-gray-400 mt-2">
      Fingerprint: <code class="text-app-teal-100">{{ revealedSecret.fingerprint }}</code> ·
      Length: {{ revealedSecret.length }} ·
      Created: {{ fmtDate(revealedSecret.created_at) }}
    </div>
    <div class="flex justify-end gap-2 mt-4">
      <button class="px-3 py-1 border border-gray-300 dark:border-[#434863] rounded text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-app-blue-100/50"
              (click)="copyRevealedSecret()">
        <i class="fa-solid fa-copy mr-1"></i>Copy
      </button>
      <button class="px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700"
              (click)="dismissReveal()">
        I've saved it
      </button>
    </div>
  </div>
</div>

<!-- Share token plaintext reveal — same z-60 over the popup as the
     secret reveal. Plaintext is returned ONCE at create time and the
     server keeps only the sha256 hash. -->
<div *ngIf="psRevealedShareToken"
     class="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-xl max-w-lg w-full border-2 border-amber-500 p-4"
       (click)="$event.stopPropagation()">
    <h3 class="text-base font-bold text-amber-600 dark:text-amber-400 mb-2">
      <i class="fa-solid fa-triangle-exclamation mr-1"></i>Share link created — shown ONCE
    </h3>
    <p class="text-xs text-gray-700 dark:text-gray-300 mb-2">
      Copy this URL now and send it to the recipient. The token is a bearer credential &mdash;
      once you dismiss this dialog, the plaintext cannot be retrieved again
      (only the hash is stored server-side). Recipients still need an F2 login
      to redeem the link &mdash; visiting it unauthenticated bounces them to <code>/login?next=…</code>.
    </p>
    <div class="text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Share URL ({{ psRevealedShareToken.label }})</div>
    <pre class="bg-gray-100 dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded p-2 font-mono text-xs break-all text-gray-900 dark:text-white">{{ psRevealedShareToken.full_url }}</pre>
    <div class="flex justify-end gap-2 mt-4">
      <button class="px-3 py-1 border border-gray-300 dark:border-[#434863] rounded text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-app-blue-100/50"
              (click)="psCopyShareToken()">
        <i class="fa-solid fa-copy mr-1"></i>Copy URL
      </button>
      <button class="px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700"
              (click)="psDismissShareToken()">
        I've saved it
      </button>
    </div>
  </div>
</div>

<!-- Delete confirm -->
<div *ngIf="showDeleteConfirm"
     class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
     (click)="cancelDelete()">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg max-w-sm w-full p-4 border border-gray-200 dark:border-[#434863]" (click)="$event.stopPropagation()">
    <h3 class="font-semibold mb-2 text-gray-900 dark:text-white">Delete customer?</h3>
    <p class="text-sm mb-4 text-gray-700 dark:text-gray-300">
      This removes <code class="text-gray-900 dark:text-white">{{ deleteTarget?._id }}</code> from <code class="text-gray-900 dark:text-white">F2-ADMIN.Customers</code>.
      Does NOT affect Cognito users, F2-ADMIN.Scanners, or any deployed code.
    </p>
    <div class="flex justify-end gap-2">
      <button class="px-3 py-1 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-900 dark:text-white" (click)="cancelDelete()">Cancel</button>
      <button class="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
              (click)="confirmDelete()">Delete</button>
    </div>
  </div>
</div>

<!-- ============================================================
     PORTAL SETTINGS — focused, friendly per-customer SSO editor.
     Opens from the key icon next to Edit in the Customer table.
     Same data + same save endpoint as the main edit modal, just
     laid out with plain-language sections, presets, and inline
     help so the operator never needs the integration PDF.
     ============================================================ -->
<div *ngIf="showPortalSettings && portalSettingsRecord"
     class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
     (click)="closePortalSettings()">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between bg-gray-50 dark:bg-app-black-100">
      <div>
        <h3 class="font-semibold text-gray-900 dark:text-white">
          <i class="fa-solid fa-key text-app-teal-100 mr-2"></i>Portal SSO Settings
        </h3>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          <code>{{ portalSettingsRecord._id }}</code> · {{ portalSettingsRecord.name || '—' }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button *ngIf="portalSettingsRecord.sso_portal || portalSettingsRecord.mta_endpoint_allowed || portalSettingsRecord._id === 'mta'" type="button"
                class="text-xs uppercase tracking-wide border border-app-teal-100 text-app-teal-100 rounded px-3 py-1 hover:bg-app-teal-100/10"
                (click)="psOpenInTester()" title="Open the SSO Tester in a new tab">
          <i class="fa-solid fa-flask mr-1"></i>Test now
        </button>
        <button class="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white text-lg leading-none" (click)="closePortalSettings()">&times;</button>
      </div>
    </div>

    <div class="p-4 space-y-4 text-sm">
      <!-- 1. ENABLE TOGGLE — the big decision -->
      <div class="rounded border border-gray-200 dark:border-[#434863] p-3 bg-gray-50 dark:bg-app-black-100">
        <label class="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" class="w-5 h-5"
                 [checked]="!!portalSettingsRecord.sso_portal"
                 (change)="psToggleEnabled()" />
          <div>
            <div class="font-semibold text-gray-900 dark:text-white">
              SSO portal handoff is <span [class.text-green-500]="portalSettingsRecord.sso_portal" [class.text-gray-500]="!portalSettingsRecord.sso_portal">{{ portalSettingsRecord.sso_portal ? 'ENABLED' : 'DISABLED' }}</span>
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              When enabled, the customer's portal can POST a signed JWT to
              <code class="text-app-teal-100">/rest/auth/validate-portal-token/{{ portalSettingsRecord._id }}</code>
              and receive a one-time SID URL for iframe / redirect. No second login on F2 side.
            </div>
          </div>
        </label>
      </div>

      <!-- 2. MTA ENDPOINT OPT-IN — cross-customer federation onto /validate-mta-token -->
      <div class="rounded border border-gray-200 dark:border-[#434863] p-3 bg-gray-50 dark:bg-app-black-100">
        <label class="flex items-center gap-3 cursor-pointer" [class.opacity-60]="portalSettingsRecord._id === 'mta'" [class.cursor-not-allowed]="portalSettingsRecord._id === 'mta'">
          <input type="checkbox" class="w-5 h-5"
                 [checked]="portalSettingsRecord._id === 'mta' || !!portalSettingsRecord.mta_endpoint_allowed"
                 [disabled]="portalSettingsRecord._id === 'mta'"
                 (change)="portalSettingsRecord.mta_endpoint_allowed = $any($event.target).checked" />
          <div>
            <div class="font-semibold text-gray-900 dark:text-white">
              MTA endpoint access is <span [class.text-green-500]="portalSettingsRecord._id === 'mta' || portalSettingsRecord.mta_endpoint_allowed" [class.text-gray-500]="portalSettingsRecord._id !== 'mta' && !portalSettingsRecord.mta_endpoint_allowed">{{ (portalSettingsRecord._id === 'mta' || portalSettingsRecord.mta_endpoint_allowed) ? 'ALLOWED' : 'BLOCKED' }}</span>
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              <ng-container *ngIf="portalSettingsRecord._id === 'mta'">
                MTA owns <code class="text-app-teal-100">/rest/auth/validate-mta-token</code> — implicitly allowed and cannot be turned off.
              </ng-container>
              <ng-container *ngIf="portalSettingsRecord._id !== 'mta'">
                Cross-customer federation — allows any of this customer's scanners to be reached via
                <code class="text-app-teal-100">/rest/auth/validate-mta-token</code> when MTA's portal mints a token whose <code>scanner</code> claim resolves (by <code>_id</code> or aliases) to one of this customer's Scanners rows.
                The owning-customer slug is derived from <code>Scanners.Client</code>; this flag is the opt-in that lets MTA's endpoint route to that resolved customer.
              </ng-container>
            </div>
          </div>
        </label>
      </div>

      <ng-container *ngIf="portalSettingsRecord.sso_portal">
        <!-- 2. QUICK START PRESETS — one-click defaults for common shapes -->
        <div class="rounded border border-amber-200 dark:border-amber-700/40 p-3 bg-amber-50 dark:bg-amber-900/20">
          <div class="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 font-semibold mb-1">
            <i class="fa-solid fa-bolt mr-1"></i>Quick start
          </div>
          <p class="text-xs text-gray-700 dark:text-gray-300 mb-2">
            Pick a preset for common customer shapes. You can still tweak any field below.
          </p>
          <div class="flex flex-wrap gap-2">
            <button type="button" class="text-xs px-3 py-1.5 rounded bg-white dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-900 dark:text-white"
                    (click)="applyPreset('jwtsso')"
                    title="Simpler Trading-style: email + name + memberships + perms + type='JWTSSO'">
              <i class="fa-solid fa-wand-magic-sparkles mr-1 text-blue-500"></i>JWTSSO standard
              <span class="block text-[10px] text-gray-500 font-normal">Simpler-style portal contract</span>
            </button>
            <button type="button" class="text-xs px-3 py-1.5 rounded bg-white dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-900 dark:text-white"
                    (click)="applyPreset('legacy')"
                    title="TheoTrade/MTA-style: separated first/last names, scanner via JWT alias, no perms, no type">
              <i class="fa-solid fa-wand-magic-sparkles mr-1 text-purple-500"></i>Legacy claim-based
              <span class="block text-[10px] text-gray-500 font-normal">TheoTrade-style portal contract</span>
            </button>
            <button type="button" class="text-xs px-3 py-1.5 rounded bg-white dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-900 dark:text-white"
                    (click)="applyPreset('theov2')"
                    title="TheoTrade v2: minimal payload with base64 email/phone, separated names, no scanner in JWT (members-page fallback), per-JWT allow_create_account">
              <i class="fa-solid fa-wand-magic-sparkles mr-1 text-green-500"></i>TheoTrade v2 (minimal + base64)
              <span class="block text-[10px] text-gray-500 font-normal">PDF v2 contract</span>
            </button>
          </div>
        </div>

        <!-- 3. INTEGRATION MODES -->
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">How will the customer load the scanner?</div>
          <div class="flex gap-4 text-sm">
            <label class="flex items-center gap-2 text-gray-900 dark:text-white cursor-pointer">
              <input type="checkbox" [checked]="psIsEmbedMode('redirect')" (change)="psToggleEmbedMode('redirect')" />
              <span>Top-level redirect (SSO)</span>
            </label>
            <label class="flex items-center gap-2 text-gray-900 dark:text-white cursor-pointer">
              <input type="checkbox" [checked]="psIsEmbedMode('iframe')" (change)="psToggleEmbedMode('iframe')" />
              <span>Iframe (embedded in their site)</span>
            </label>
          </div>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            Both produce the same handoff URL &mdash; this tells the customer's portal devs which modes you support.
          </p>
        </div>

        <!-- 3b. HANDOFF MODE — sid (default) vs ?token= opt-in -->
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">SSO handoff</div>
          <label class="flex items-start gap-2 cursor-pointer text-sm text-gray-900 dark:text-white">
            <input type="checkbox"
                   class="mt-0.5"
                   [checked]="portalSettingsRecord.sso_portal.handoff_mode === 'token_query'"
                   (change)="portalSettingsRecord.sso_portal.handoff_mode = $any($event.target).checked ? 'token_query' : 'sid'" />
            <span>
              <strong>Allow <code>?token=</code> redirects</strong> &mdash; emit the handoff URL as
              <code>?token=&lt;jwt&gt;</code> instead of the default <code>?sid=&lt;43char&gt;</code>.
            </span>
          </label>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-1 ml-6">
            Default (unchecked) is the secure <strong>opaque SID</strong> handoff &mdash; a short-lived single-use 43-char ID exchanged server-side for the Cognito bundle.
            Check this only when the customer's SPA still requires the legacy <code>?token=</code> shape. The JWT is signed with this customer's stored HS256 shared secret
            (same secret used to verify their inbound JWTs &mdash; symmetric trust) and wraps an underlying single-use SID, so the redeem path is identical to the SID flow.
            Trade-off: the JWT enters browser history, server logs, and any <code>Referer</code> headers from the scanner page.
          </p>
          <div *ngIf="portalSettingsRecord.sso_portal.handoff_mode === 'token_query'" class="mt-2 ml-6">
            <label class="block text-[11px] uppercase text-gray-500 dark:text-gray-400 mb-1">
              Token <code>exp</code> lifetime (seconds) &mdash; default 3600 (1 hour) if empty
            </label>
            <input type="number" min="30" max="86400" step="30"
                   [(ngModel)]="portalSettingsRecord.sso_portal.token_query_exp_seconds"
                   placeholder="3600"
                   class="w-40 border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm font-mono" />
            <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
              The JWT expires this many seconds after it's signed. Bump high (3600+) for manual operator testing or URL paste-around;
              tighten (60-300) for portal-to-SPA flows where the browser navigates immediately.
            </p>
          </div>
        </div>

        <!-- 3c. SID REDEEM GRACE — iframe-refresh survival window -->
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Iframe-refresh survival</div>
          <label class="block text-[11px] uppercase text-gray-500 dark:text-gray-400 mb-1">
            SID redeem grace (seconds) &mdash; default 0 (strict single-use)
          </label>
          <input type="number" min="0" max="90" step="5"
                 [(ngModel)]="portalSettingsRecord.sso_portal.sid_redeem_grace_seconds"
                 placeholder="0"
                 class="w-40 border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm font-mono" />
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            <strong>0</strong> (default) &mdash; the SID is destroyed on the first <code>/redeem-session</code> call (Redis <code>GETDEL</code>). Every existing customer is on this setting.<br/>
            <strong>&gt; 0</strong> &mdash; the SID stays redeemable for this many seconds after the first call (Redis <code>EXPIRE LT</code>). Use this when the customer's <strong>parent page holds the iframe with <code>?sid=</code> in <code>src</code></strong> &mdash; a user-initiated refresh remounts the iframe with the same SID URL, which would otherwise <code>401</code> ("session expired").
            Recommended <strong>15-30s</strong>: long enough for a real refresh + iframe re-handshake, short enough that a leaked SID URL isn't useful for long. Hard-capped at <strong>90s</strong> (the mint TTL) &mdash; this can never extend a SID's life past its normal expiry.
          </p>
        </div>

        <!-- 4. WHICH SCANNERS — permission allowlist -->
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">
            Which scanners can SSO route into?
          </div>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
            Permission allowlist. Leave all <strong>unchecked</strong> to allow ALL of this customer's scanners (default).
            Check specific ones to restrict SSO to a subset (the others stay password-login-only).
          </p>
          <div *ngIf="psOwnedScannerIds().length === 0" class="text-xs text-amber-600 dark:text-amber-400 italic">
            This customer has no <code>scanner_ids</code>. Use the main Edit button to add them first.
          </div>
          <div *ngIf="psOwnedScannerIds().length > 0" class="flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100 rounded">
            <label *ngFor="let sid of psOwnedScannerIds()" class="text-xs text-gray-900 dark:text-white flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" [checked]="psIsScannerAllowed(sid)" (change)="psToggleScannerAllowed(sid)" />
              <code>{{ sid }}</code>
            </label>
          </div>
        </div>

        <!-- 5. SCANNER SELECTION RULE -->
        <div *ngIf="portalSettingsRecord.sso_portal.scanner_selection">
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">
            How does the JWT decide which scanner the user lands on?
          </div>
          <select class="border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs"
                  [(ngModel)]="portalSettingsRecord.sso_portal.scanner_selection.mode">
            <option value="fixed">Fixed &mdash; always the same scanner (single-scanner customers)</option>
            <option value="from_jwt">From JWT &mdash; customer's portal picks via a claim</option>
            <option value="from_membership">From membership &mdash; mapped by entitlement slug</option>
          </select>

          <div *ngIf="portalSettingsRecord.sso_portal.scanner_selection.mode === 'fixed'" class="mt-2">
            <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Scanner id (always)</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs"
                   [(ngModel)]="portalSettingsRecord.sso_portal.scanner_id" placeholder="e.g. spt-b3" />
          </div>
          <div *ngIf="portalSettingsRecord.sso_portal.scanner_selection.mode === 'from_jwt'" class="mt-2 space-y-2">
            <div>
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">JWT claim name carrying the scanner</label>
              <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.scanner_selection.scanner_claim" placeholder="scanner" />
            </div>
            <div>
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Default scanner id (used when no JWT match)</label>
              <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.scanner_id" placeholder="e.g. tt-gc" />
            </div>
            <p class="text-[11px] text-gray-500 dark:text-gray-400">
              The validator translates the JWT's value via <code>Scanners.&lt;id&gt;.SsoAliases.{{ portalSettingsRecord._id }}</code>.
              Configure aliases on the Scanners admin page (e.g. TheoTrade: <code>genesis-cog &rarr; tt-gc</code>).
            </p>
          </div>
          <div *ngIf="portalSettingsRecord.sso_portal.scanner_selection.mode === 'from_membership'" class="mt-2">
            <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">membership_map (JSON: <code>membership &rarr; scanner_id</code>)</label>
            <textarea class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs" rows="3"
                      [(ngModel)]="psMembershipMapJson"
                      placeholder='&#123;"big_3_member": "spt-b3", "pro_member": "spt-pro"&#125;'></textarea>
          </div>
        </div>

        <!-- 6. WHERE TO FIND USER IDENTITY IN THE JWT -->
        <div *ngIf="portalSettingsRecord.sso_portal.identity_mapping !== undefined">
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Where is the user's identity in the JWT?</div>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
            Leave blank for JWTSSO defaults (<code>email</code> + split <code>name</code>).
            For TheoTrade-style customers with separated names, set <code>first_name</code> + <code>last_name</code>.
            For MTA-style synthesised emails, use <code>email_template</code>.
          </p>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">email claim name</label>
              <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.identity_mapping.email" placeholder="email" />
            </div>
            <div>
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">email template (overrides above)</label>
              <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.identity_mapping.email_template" placeholder="&#123;user_identifier&#125;&#64;mta.com" />
            </div>
            <div>
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">first_name claim</label>
              <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.identity_mapping.first_name" placeholder="first_name" />
            </div>
            <div>
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">last_name claim</label>
              <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.identity_mapping.last_name" placeholder="last_name" />
            </div>
            <div class="col-span-2">
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">phone claim (optional &mdash; stored on Cognito as <code>phone_number</code> when present)</label>
              <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.identity_mapping.phone" placeholder="phone" />
            </div>
          </div>
        </div>

        <!-- 7. JWT CONTRACT — claims the customer's portal signs -->
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">What claims must the JWT carry?</div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">Expected <code>type</code> claim (leave blank to skip)</label>
              <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.expected_type" placeholder="JWTSSO (or empty)" />
            </div>
            <div>
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">SID TTL (seconds)</label>
              <input type="number" min="30" max="300"
                     class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.sid_ttl_seconds" placeholder="90" />
            </div>
          </div>
          <div class="mt-2">
            <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">Required claims (comma-separated)</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                   [(ngModel)]="psRequiredClaimsText"
                   placeholder="email, name, memberships, perms, type, iat, exp" />
          </div>
          <div class="mt-2">
            <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">Allowed memberships (comma-separated &mdash; leave empty to skip the check)</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                   [(ngModel)]="psAllowedMembershipsText"
                   placeholder="big_3_market_scorecard, big_3_market_scorecard_temp" />
          </div>
          <!-- v2: optional claims + base64 fields + freshness window -->
          <div class="mt-2">
            <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">Optional claims (comma-separated &mdash; may be present but not required)</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                   [(ngModel)]="psOptionalClaimsText"
                   placeholder="phone, allow_create_account" />
          </div>
          <div class="mt-2">
            <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">Base64-encoded claim names (comma-separated &mdash; validator auto-decodes)</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                   [(ngModel)]="psBase64FieldsText"
                   placeholder="email, phone" />
          </div>
          <div class="mt-2 grid grid-cols-2 gap-3">
            <div>
              <label class="flex items-center gap-2 cursor-pointer text-xs text-gray-900 dark:text-white pt-3">
                <input type="checkbox" [(ngModel)]="portalSettingsRecord.sso_portal.allow_create_account_enabled" />
                <span>Honor JWT's <code>allow_create_account</code> setting</span>
              </label>
              <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                When ON, validator reads <code>payload.allow_create_account</code> and refuses to mint a new Cognito user when it's <code>false</code>. When OFF, today's implicit auto-create runs.
              </p>
            </div>
            <div>
              <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400">Max iat age (seconds; for JWTs without exp)</label>
              <input type="number" min="0" max="600"
                     class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                     [(ngModel)]="portalSettingsRecord.sso_portal.max_iat_age_seconds"
                     placeholder="60" />
            </div>
          </div>
        </div>

        <!-- 8. DEFAULT SCANNERS — chip-style multi-select + wildcard freeform -->
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Default scanners granted on new-user SSO auto-create</div>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
            Applied ONLY when SSO auto-creates a new user AND the JWT has no <code>scanner</code> field.
            Each entry is either a concrete scanner from this customer's catalog OR a wildcard pattern
            ending in <code>*</code> (e.g. <code>tt-*</code> = "every scanner whose ID starts with tt-").
            Stored literally; downstream access checks match wildcards at read time.
          </p>

          <!-- Current entries as chips with delete -->
          <div class="flex flex-wrap gap-1.5 mb-2 min-h-[28px] p-1.5 border border-gray-300 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100 rounded">
            <span *ngFor="let entry of psDefaultScannersList"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono"
                  [class.bg-amber-100]="entry.endsWith('*')"
                  [class.dark:bg-amber-900]="entry.endsWith('*')"
                  [class.text-amber-900]="entry.endsWith('*')"
                  [class.dark:text-amber-200]="entry.endsWith('*')"
                  [class.bg-blue-100]="!entry.endsWith('*')"
                  [class.dark:bg-app-blue-100]="!entry.endsWith('*')"
                  [class.text-blue-900]="!entry.endsWith('*')"
                  [class.dark:text-blue-200]="!entry.endsWith('*')">
              <i *ngIf="entry.endsWith('*')" class="fa-solid fa-asterisk text-[8px]" title="Wildcard pattern"></i>
              {{ entry }}
              <button type="button"
                      class="ml-1 hover:text-red-600 dark:hover:text-red-400"
                      (click)="psRemoveDefaultScanner(entry)"
                      [attr.title]="'Remove ' + entry">
                <i class="fa-solid fa-xmark text-[10px]"></i>
              </button>
            </span>
            <span *ngIf="psDefaultScannersList.length === 0" class="text-[11px] text-gray-400 italic px-1">
              (none — JWT-specified scanners only)
            </span>
          </div>

          <!-- Add from this customer's catalog -->
          <div class="flex gap-2 items-center mb-2" *ngIf="psAvailableScannerOptions().length > 0">
            <label class="text-[10px] uppercase text-gray-500 dark:text-gray-400 whitespace-nowrap">Add from catalog:</label>
            <select #addPick
                    class="flex-1 border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                    (change)="psAddDefaultScanner(addPick.value); addPick.value = ''">
              <option value="">— pick a scanner —</option>
              <option *ngFor="let sid of psAvailableScannerOptions()" [value]="sid">{{ sid }}</option>
            </select>
          </div>

          <!-- Wildcard / custom entry freeform -->
          <div class="flex gap-2 items-center">
            <label class="text-[10px] uppercase text-gray-500 dark:text-gray-400 whitespace-nowrap">Or wildcard:</label>
            <input class="flex-1 border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                   [(ngModel)]="psDefaultScannersInput"
                   (keydown.enter)="psAddDefaultScanner(psDefaultScannersInput); $event.preventDefault()"
                   placeholder="tt-*" />
            <button type="button"
                    class="dark-btn dark-btn-sm"
                    [disabled]="!psDefaultScannersInput.trim()"
                    (click)="psAddDefaultScanner(psDefaultScannersInput)">
              <i class="fa-solid fa-plus mr-1"></i>Add
            </button>
          </div>
        </div>

        <!-- 8b. SUPPORT CONTACT HTML — kept as schema for future error-surface use -->
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Customer support contact (HTML) <span class="text-[10px] text-gray-400 italic normal-case">— reserved</span></div>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
            Customer-supplied HTML reserved for future error-surface use (account-link disambiguation, suspended-user redirect, etc.). Currently no SSO failure path reaches this field &mdash; the previous EMAIL_CONFLICT guard was retired when <code>allow_create_account=true</code>+existing-email became an additive-update success case.
            Supports hyperlinks &mdash; e.g. <code>Contact &lt;a href="mailto:support&#64;example.com"&gt;support&#64;example.com&lt;/a&gt;</code>.
          </p>
          <textarea class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs" rows="3"
                    [(ngModel)]="psSupportContactHtml"
                    placeholder='Contact &lt;a href="mailto:support@theotrade.com"&gt;support@theotrade.com&lt;/a&gt; to merge your accounts.'></textarea>
        </div>

        <!-- 9. CLAIM TEMPLATE — what gets written to Cognito custom:claims -->
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">What goes into the user's Cognito record?</div>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
            JSON template for the per-scanner <code>custom:claims</code> entry. Use
            <code>&#123;scanner_id&#125;</code>, <code>&#123;payload.&lt;claim&gt;&#125;</code>, or
            <code>&#123;perms.&lt;key&gt;&#125;</code> placeholders. Leave empty to use the default
            <code>&#123;"scanner":"&#123;scanner_id&#125;","live_data":"true"&#125;</code>.
            Auto-applied once per scanner granted by default_scanners.
          </p>
          <textarea class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 font-mono text-xs" rows="3"
                    [(ngModel)]="psClaimTemplateJson"></textarea>
        </div>

        <!-- 9a. CUSTOMER ASSETS — uploaded integration docs (HTML / PDF / MD).
             Lives in F2-ADMIN.CustomerAssets as BinData (<=700KB binary).
             Auto-fills docs_url below on upload so the SSO Tester's
             "Open guide" link resolves to the just-uploaded asset. -->
        <div class="border-t border-gray-200 dark:border-[#434863] pt-3">
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">
            <i class="fa-solid fa-paperclip mr-1"></i>Integration documents
          </div>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
            HTML / PDF / Markdown guides attached to this customer. Uploads land in
            <code>F2-ADMIN.CustomerAssets</code> with per-role visibility, role-gated serve, and an
            HMAC-signed audit trail. <strong>Per-file cap: {{ ASSET_MAX_BYTES / 1024 }}&nbsp;KB binary.</strong>
            Uploading auto-fills the Documentation URL below so the SSO Tester's "Open guide" link
            picks it up immediately.
          </p>

          <!-- Asset list -->
          <div *ngIf="psAssetsLoading" class="text-xs text-gray-500 mb-2">Loading assets…</div>
          <div *ngIf="!psAssetsLoading && psAssets.length === 0" class="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
            No assets uploaded yet.
          </div>
          <table *ngIf="psAssets.length > 0" class="w-full text-xs mb-3">
            <thead>
              <tr class="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863]">
                <th class="py-1 pr-2 w-6"></th>
                <th class="py-1 pr-2">Label / filename</th>
                <th class="py-1 pr-2">Size</th>
                <th class="py-1 pr-2">Visibility</th>
                <th class="py-1 pr-2">Shares</th>
                <th class="py-1 pr-2">Uploaded</th>
                <th class="py-1 pr-2 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <ng-container *ngFor="let a of psAssets">
                <tr class="border-b border-gray-200 dark:border-[#434863] align-top">
                  <td class="py-1 pr-2 text-center">
                    <i class="fa-solid" [ngClass]="assetIcon(a.content_type)" [title]="a.content_type"></i>
                  </td>
                  <td class="py-1 pr-2">
                    <div class="text-gray-900 dark:text-white font-medium">{{ a.label }}</div>
                    <a class="text-app-teal-100 hover:underline font-mono text-[11px]"
                       [href]="psAssetServeUrl(a)" target="_blank" rel="noopener" [title]="'Open ' + a.filename + ' in a new tab'">
                      <i class="fa-solid fa-arrow-up-right-from-square mr-0.5"></i>{{ a.filename }}
                    </a>
                  </td>
                  <td class="py-1 pr-2 text-gray-700 dark:text-gray-300">{{ fmtBytes(a.size) }}</td>
                  <td class="py-1 pr-2">
                    <span *ngFor="let v of a.visibility"
                          class="inline-block text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 mr-1 mb-0.5"
                          [ngClass]="{
                            'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300': v === 'admin',
                            'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': v === 'client_admin',
                            'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': v === 'member'
                          }">
                      {{ v }}
                    </span>
                  </td>
                  <td class="py-1 pr-2 text-gray-700 dark:text-gray-300">
                    <span *ngIf="activeShareCount(a) > 0" class="text-app-teal-100">{{ activeShareCount(a) }} active</span>
                    <span *ngIf="activeShareCount(a) === 0 && a.share_tokens.length > 0" class="text-gray-400">0 active / {{ a.share_tokens.length }} total</span>
                    <span *ngIf="a.share_tokens.length === 0" class="text-gray-400">—</span>
                  </td>
                  <td class="py-1 pr-2 text-gray-700 dark:text-gray-300">
                    <div>{{ fmtDate(a.uploaded_at) }}</div>
                    <div class="text-[10px] text-gray-500">{{ a.uploaded_by_email }}</div>
                  </td>
                  <td class="py-1 pr-2 text-right whitespace-nowrap">
                    <button type="button" class="text-blue-500 hover:text-blue-700 mr-2" title="Create share link"
                            (click)="psOpenShareForm(a)">
                      <i class="fa-solid fa-share-nodes"></i>
                    </button>
                    <button type="button" class="text-gray-500 hover:text-gray-800 dark:hover:text-white mr-2" title="Audit trail"
                            (click)="psToggleAudit(a)">
                      <i class="fa-solid" [ngClass]="psAssetAudit[a.id]?.open ? 'fa-chevron-up' : 'fa-clock-rotate-left'"></i>
                    </button>
                    <button type="button" class="text-red-500 hover:text-red-700" title="Delete asset"
                            (click)="psDeleteAsset(a)">
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </td>
                </tr>

                <!-- Share-tokens sub-row — only when this asset has any -->
                <tr *ngIf="a.share_tokens.length > 0" class="border-b border-gray-200 dark:border-[#434863] bg-gray-50/50 dark:bg-app-black-100/40">
                  <td></td>
                  <td colspan="6" class="py-1 pr-2">
                    <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Share links</div>
                    <table class="w-full text-[11px]">
                      <thead>
                        <tr class="text-left text-gray-500 dark:text-gray-400">
                          <th class="py-0.5 pr-2">Label</th>
                          <th class="py-0.5 pr-2">Created by</th>
                          <th class="py-0.5 pr-2">Created</th>
                          <th class="py-0.5 pr-2">Expires</th>
                          <th class="py-0.5 pr-2">Status</th>
                          <th class="py-0.5 pr-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let s of a.share_tokens" class="border-t border-gray-200 dark:border-[#434863]">
                          <td class="py-0.5 pr-2 text-gray-800 dark:text-gray-200">{{ s.label || '—' }}</td>
                          <td class="py-0.5 pr-2 text-gray-700 dark:text-gray-300">{{ s.created_by_email }}</td>
                          <td class="py-0.5 pr-2 text-gray-700 dark:text-gray-300">{{ fmtDate(s.created_at) }}</td>
                          <td class="py-0.5 pr-2 text-gray-700 dark:text-gray-300">{{ s.expires_at ? fmtDate(s.expires_at) : 'never' }}</td>
                          <td class="py-0.5 pr-2">
                            <span *ngIf="s.active" class="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">active</span>
                            <span *ngIf="!s.active && s.revoked_at" class="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">revoked</span>
                            <span *ngIf="!s.active && !s.revoked_at" class="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">expired</span>
                          </td>
                          <td class="py-0.5 pr-2 text-right">
                            <button *ngIf="s.active" type="button" class="text-red-500 hover:text-red-700" title="Revoke"
                                    (click)="psRevokeShareToken(a, s.token_hash)">
                              <i class="fa-solid fa-ban"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                <!-- Audit trail sub-row -->
                <tr *ngIf="psAssetAudit[a.id]?.open" class="border-b border-gray-200 dark:border-[#434863] bg-gray-50/50 dark:bg-app-black-100/40">
                  <td></td>
                  <td colspan="6" class="py-1 pr-2">
                    <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">
                      <i class="fa-solid fa-shield-halved mr-1"></i>Audit trail
                      <span class="ml-1 text-gray-400 normal-case italic">HMAC-signed, displayed raw</span>
                    </div>
                    <div *ngIf="psAssetAudit[a.id]?.loading" class="text-xs text-gray-500">Loading audit…</div>
                    <div *ngIf="!psAssetAudit[a.id]?.loading && psAssetAudit[a.id]?.rows?.length === 0" class="text-xs text-gray-500 italic">
                      No audit rows.
                    </div>
                    <ol *ngIf="(psAssetAudit[a.id]?.rows?.length || 0) > 0" class="space-y-1 border-l-2 border-gray-300 dark:border-[#434863] pl-3">
                      <li *ngFor="let r of psAssetAudit[a.id]?.rows" class="text-[11px]">
                        <div class="flex items-baseline gap-2 flex-wrap">
                          <span class="font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 text-[10px]"
                                [ngClass]="{
                                  'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300': r.action === 'upload',
                                  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': r.action === 'delete',
                                  'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': r.action === 'share_create',
                                  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300': r.action === 'share_revoke',
                                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300': r.action === 'view'
                                }">{{ r.action }}</span>
                          <span class="text-gray-800 dark:text-gray-200">{{ r.actor_email }}</span>
                          <span class="text-gray-500">{{ fmtDate(r.at) }}</span>
                        </div>
                        <div *ngIf="r.details" class="text-gray-600 dark:text-gray-400 font-mono text-[10px] break-all">
                          {{ r.details | json }}
                        </div>
                        <div class="text-gray-400 dark:text-gray-500 font-mono text-[10px] break-all" title="HMAC-SHA256 signature">
                          sig: {{ r.signature }}
                        </div>
                      </li>
                    </ol>
                  </td>
                </tr>
              </ng-container>
            </tbody>
          </table>

          <!-- Inline share-link create form (above the upload widget so it's
               visible while the operator types the label). -->
          <div *ngIf="psShareFormAssetId"
               class="border border-blue-300 dark:border-blue-700 rounded p-2 mb-3 bg-blue-50 dark:bg-blue-900/20">
            <div class="text-[11px] uppercase tracking-wide text-blue-700 dark:text-blue-300 font-semibold mb-1">
              <i class="fa-solid fa-share-nodes mr-1"></i>Create share link
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <div>
                <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Label (required)</label>
                <input type="text" [(ngModel)]="psShareFormLabel"
                       placeholder="e.g. 'portal-team-2026Q3' or 'temporary-review-link'"
                       class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs" />
              </div>
              <div>
                <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Expires at (optional — empty = never)</label>
                <input type="datetime-local" [(ngModel)]="psShareFormExpires"
                       class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs" />
              </div>
            </div>
            <div class="flex justify-end gap-2">
              <button type="button" class="px-3 py-1 border border-gray-300 dark:border-[#434863] rounded text-gray-900 dark:text-white text-xs hover:bg-gray-100 dark:hover:bg-app-blue-100/50"
                      (click)="psCloseShareForm()">Cancel</button>
              <button type="button" class="px-3 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700"
                      (click)="psCreateShareLink()">
                <i class="fa-solid fa-plus mr-1"></i>Create share link
              </button>
            </div>
          </div>

          <!-- Upload widget -->
          <div class="border border-gray-200 dark:border-[#434863] rounded p-2 bg-gray-50 dark:bg-app-black-100">
            <div class="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">
              <i class="fa-solid fa-cloud-arrow-up mr-1"></i>Upload new asset
            </div>
            <div class="space-y-2">
              <div>
                <input type="file" accept=".html,.htm,.pdf,.md,.markdown"
                       (change)="onAssetFilePicked($event)"
                       class="block w-full text-xs text-gray-700 dark:text-gray-300
                              file:mr-2 file:py-1 file:px-3 file:rounded file:border-0
                              file:text-xs file:font-semibold
                              file:bg-blue-50 file:text-blue-700
                              hover:file:bg-blue-100" />
                <div *ngIf="psAssetUploadFile" class="text-[10px] text-gray-500 mt-1">
                  Selected: <code>{{ psAssetUploadFile.name }}</code>
                  ({{ fmtBytes(psAssetUploadFile.size) }}, {{ psAssetUploadFile.type || 'unknown type' }})
                </div>
              </div>
              <div>
                <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Label (defaults to filename)</label>
                <input type="text" [(ngModel)]="psAssetUploadLabel"
                       placeholder="Human-readable label shown in the list"
                       class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs" />
              </div>
              <div>
                <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Category</label>
                <input type="text" [(ngModel)]="psAssetUploadCategory"
                       placeholder="sso-integration"
                       class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs" />
              </div>
              <div>
                <div class="text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Visible to</div>
                <div class="flex flex-wrap gap-3 text-xs">
                  <label class="flex items-center gap-1.5 cursor-pointer text-gray-900 dark:text-white">
                    <input type="checkbox" [(ngModel)]="psAssetUploadVisibility.admin" />
                    <span>admin</span>
                  </label>
                  <label class="flex items-center gap-1.5 cursor-pointer text-gray-900 dark:text-white">
                    <input type="checkbox" [(ngModel)]="psAssetUploadVisibility.client_admin" />
                    <span>client_admin</span>
                  </label>
                  <label class="flex items-center gap-1.5 cursor-pointer text-gray-900 dark:text-white">
                    <input type="checkbox" [(ngModel)]="psAssetUploadVisibility.member" />
                    <span>member</span>
                  </label>
                </div>
              </div>
              <div class="flex justify-end">
                <button type="button"
                        class="px-3 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                        [disabled]="!psAssetUploadFile"
                        (click)="psUploadAsset()">
                  <i class="fa-solid fa-cloud-arrow-up mr-1"></i>Upload asset
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 9b. DOCS URL — the actual docs_url value that downstream consumers
             (SSO Tester, portal-handoff response surfaces, etc.) read. Three
             ways to populate: upload above (auto-fills), one-click live-render
             quick-fill, or paste a hand-curated URL. Open button lets the
             operator preview the linked doc in a new tab without leaving the
             popup. -->
        <div>
          <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Documentation URL (integration guide for this customer's portal devs)</label>
          <div class="flex gap-1.5 items-stretch">
            <input class="flex-1 border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-1.5 py-0.5 font-mono text-xs"
                   [(ngModel)]="portalSettingsRecord.sso_portal.docs_url"
                   placeholder="/rest/admin/customer-assets/{{ portalSettingsRecord._id }}/integration-guide" />
            <a *ngIf="portalSettingsRecord.sso_portal.docs_url && !psLiveGuideBlockedReason()"
               [href]="psDocsUrlPreviewHref()"
               target="_blank" rel="noopener"
               class="dark-btn dark-btn-sm whitespace-nowrap"
               title="Open the linked documentation in a new tab (the portal-handoff surface receives this URL).">
              <i class="fa-solid fa-arrow-up-right-from-square mr-1"></i>Open
            </a>
            <button *ngIf="portalSettingsRecord.sso_portal.docs_url && psLiveGuideBlockedReason()"
                    type="button"
                    class="dark-btn dark-btn-sm whitespace-nowrap opacity-50 cursor-not-allowed"
                    disabled
                    [title]="psLiveGuideBlockedReason()">
              <i class="fa-solid fa-arrow-up-right-from-square mr-1"></i>Open
            </button>
            <button type="button"
                    class="dark-btn dark-btn-sm whitespace-nowrap"
                    [class.opacity-50]="!!psLiveGuideBlockedReason()"
                    [class.cursor-not-allowed]="!!psLiveGuideBlockedReason()"
                    [disabled]="!!psLiveGuideBlockedReason()"
                    (click)="psUseLiveRenderedGuide()"
                    [title]="psLiveGuideBlockedReason() || 'Set docs_url to the live-rendered integration guide endpoint for this customer. The doc renders from F2-ADMIN.Customers.sso_portal + Scanners live on every request, so it can never drift from the validator\'s contract.'">
              <i class="fa-solid fa-bolt mr-1"></i>Use live guide
            </button>
          </div>
          <p *ngIf="psLiveGuideBlockedReason()"
             class="text-[11px] text-amber-500 dark:text-amber-400 mt-1 flex items-start gap-1">
            <i class="fa-solid fa-triangle-exclamation mt-0.5"></i>
            <span>{{ psLiveGuideBlockedReason() }}</span>
          </p>
          <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            What downstream consumers receive at portal handoff. Three sources, same field: uploads above auto-fill it; the
            <strong>Use live guide</strong> button sets it to the per-customer rendered endpoint
            (<code>/rest/admin/customer-assets/{{ portalSettingsRecord._id }}/integration-guide</code>) which always reflects the current sso_portal config + Scanners rows;
            or paste a hand-curated external URL. The <strong>Open</strong> button previews exactly what a portal team would see.
          </p>
        </div>

        <!-- 10. SHARED SECRETS -->
        <div class="border-t border-gray-200 dark:border-[#434863] pt-3">
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Shared secrets</div>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
            HS256 keys the customer's portal uses to sign JWTs. AES-256-GCM encrypted at rest.
            Plaintext shown <strong>once on generate</strong> &mdash; share via a one-time-secret link.
          </p>
          <div *ngIf="ssoSecretsLoading" class="text-xs text-gray-500 mb-2">Loading…</div>
          <div *ngIf="!ssoSecretsLoading && ssoSecrets.length === 0" class="text-xs text-amber-600 dark:text-amber-400 mb-2">
            No active secrets &mdash; generate one to activate this integration.
          </div>
          <table *ngIf="ssoSecrets.length > 0" class="w-full text-xs mb-2">
            <thead>
              <tr class="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863]">
                <th class="py-1 pr-2">Fingerprint</th>
                <th class="py-1 pr-2">Label</th>
                <th class="py-1 pr-2">Created</th>
                <th class="py-1 pr-2">By</th>
                <th class="py-1 pr-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let s of ssoSecrets" class="border-b border-gray-200 dark:border-[#434863]">
                <td class="py-1 pr-2 font-mono text-app-teal-100">{{ s.fingerprint }}</td>
                <td class="py-1 pr-2 text-gray-700 dark:text-gray-300">{{ s.label || '—' }}</td>
                <td class="py-1 pr-2 text-gray-700 dark:text-gray-300">{{ fmtDate(s.created_at) }}</td>
                <td class="py-1 pr-2 text-gray-700 dark:text-gray-300">{{ s.created_by_email || '—' }}</td>
                <td class="py-1 pr-2">
                  <button type="button" class="text-red-500 hover:text-red-700" title="Delete" (click)="psDeleteSecret(s.id)">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="flex items-center gap-2 mb-3">
            <input type="text" [(ngModel)]="revealSecretLabel" placeholder="Optional label (e.g. 'initial', 'from-portal-2026Q3')"
                   class="flex-1 border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs" />
            <button type="button" class="px-3 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700" (click)="psGenerateSecret()">
              <i class="fa-solid fa-plus mr-1"></i>Generate new secret
            </button>
          </div>

          <!-- Import an existing operator-supplied plaintext secret. Has
               its OWN label field (not reusing the Generate label above)
               so the form is fully self-contained — operator doesn't need
               to scroll back up. Pasted plaintext gets encrypted +
               fingerprinted server-side and pushed to sso_portal.secrets[]. -->
          <div class="border-t border-gray-200 dark:border-[#434863] pt-3">
            <div class="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">
              <i class="fa-solid fa-file-import mr-1"></i>Import existing secret
            </div>
            <p class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
              Use when the customer's portal already signs with a known secret (you have the plaintext from them or generated it out-of-band). The plaintext is encrypted under the F2 envelope and added alongside any existing secrets &mdash; the customer can keep using their value. Duplicate fingerprints are rejected.
            </p>
            <div class="space-y-2">
              <div>
                <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Label for this imported secret</label>
                <input type="text" [(ngModel)]="importSecretLabel"
                       placeholder="e.g. 'from-theotrade-2026-06-15' or 'customer-supplied'"
                       class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs" />
              </div>
              <div>
                <label class="block text-[10px] uppercase text-gray-500 dark:text-gray-400 mb-1">Plaintext secret</label>
                <textarea rows="2" [(ngModel)]="importSecretPlaintext"
                          placeholder="Paste the customer's existing HS256 shared secret here"
                          class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs font-mono"></textarea>
              </div>
              <div class="flex justify-end">
                <button type="button"
                        class="px-3 py-1 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
                        [disabled]="!importSecretPlaintext.trim()"
                        (click)="psImportSecret()">
                  <i class="fa-solid fa-file-import mr-1"></i>Import secret
                </button>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Footer -->
    <div class="px-4 py-3 border-t border-gray-200 dark:border-[#434863] flex justify-end gap-2 bg-gray-50 dark:bg-app-black-100">
      <button class="px-3 py-1 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-900 dark:text-white" (click)="closePortalSettings()">Cancel</button>
      <button class="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700" (click)="psSave()">Save portal settings</button>
    </div>
  </div>
</div>

<!-- Payments & E-Commerce popup -->
<div *ngIf="showCommerceSettings && commerceRecord"
     class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
     (click)="closeCommerceSettings()">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">

    <div class="px-4 py-3 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between">
      <div>
        <h3 class="font-semibold text-gray-900 dark:text-white">
          <i class="fa-solid fa-dollar-sign mr-2 text-green-600 dark:text-green-400"></i>
          Payments &amp; E-Commerce
        </h3>
        <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">
          {{ commerceRecord._id }}
          <ng-container *ngIf="commerceRecord.name"> &middot; {{ commerceRecord.name }}</ng-container>
        </div>
      </div>
      <button class="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white" (click)="closeCommerceSettings()">✕</button>
    </div>

    <div class="p-4 space-y-5 text-sm">

      <div class="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
        Configure inbound webhook intake for each storefront / payment system this customer uses.
        Each integration is wired independently. Webhook secrets must match the value configured on the
        upstream side (WC, Stripe, etc.) — copy / paste / generate manages that string here.
      </div>

      <!-- Per-integration sections -->
      <ng-container *ngFor="let integ of COMMERCE_INTEGRATIONS">
        <div class="border border-gray-200 dark:border-[#434863] rounded">
          <div class="px-4 py-2 bg-gray-50 dark:bg-app-black-100 flex items-center justify-between rounded-t">
            <div class="flex items-center gap-3">
              <h4 class="font-semibold text-gray-900 dark:text-white">{{ integ.label }}</h4>
              <span *ngIf="!integ.ready" class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Coming soon</span>
              <span *ngIf="integ.ready && commerceUi[integ.key]?.enabled" class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">Enabled</span>
              <span *ngIf="integ.ready && !commerceUi[integ.key]?.enabled" class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Disabled</span>
            </div>
            <label *ngIf="integ.ready" class="inline-flex items-center cursor-pointer gap-2">
              <input type="checkbox" class="rounded" [(ngModel)]="commerceUi[integ.key].enabled" />
              <span class="text-xs text-gray-700 dark:text-gray-300">Enable</span>
            </label>
          </div>

          <!-- Body only renders when the integration is enabled; the
               header row's Enable toggle expands/collapses it inline. -->
          <div *ngIf="integ.ready && commerceUi[integ.key]?.enabled" class="p-4 space-y-4">

            <!-- Webhook setup (Delivery URL + Topics + Secret + Ping log + Ping stats)
                 — expandable. Collapsed by default: once wired the values
                 rarely change; only the initial customer setup and secret
                 rotations touch them. Matches the "WC REST API" section's
                 collapse UX below. -->
            <div class="border border-gray-200 dark:border-[#434863] rounded">
              <button type="button"
                      class="w-full px-3 py-2 flex items-center justify-between text-left bg-gray-50 dark:bg-app-black-100/40 hover:bg-gray-100 dark:hover:bg-app-blue-100/30"
                      (click)="commerceUi[integ.key].webhook_section_open = !commerceUi[integ.key].webhook_section_open">
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  <i class="fa-solid fa-plug mr-1"></i>Webhook setup
                  <span class="ml-2 normal-case text-[10px] text-gray-400">delivery URL, topics, secret, ping log</span>
                  <span *ngIf="commerceUi[integ.key].has_secret_stored" class="ml-2 normal-case text-[10px] text-green-600 dark:text-green-400">
                    <i class="fa-solid fa-circle-check"></i> secret stored
                  </span>
                  <span *ngIf="!commerceUi[integ.key].has_secret_stored" class="ml-2 normal-case text-[10px] text-amber-500">
                    <i class="fa-solid fa-triangle-exclamation"></i> secret not set
                  </span>
                </span>
                <i class="fa-solid" [class.fa-chevron-down]="!commerceUi[integ.key].webhook_section_open" [class.fa-chevron-up]="commerceUi[integ.key].webhook_section_open"></i>
              </button>
              <div *ngIf="commerceUi[integ.key].webhook_section_open" class="p-3 space-y-4">

            <!-- Delivery URL — auto-derived from customer slug + integration
                 key. Read-only field so it's obvious the value isn't editable
                 (change the slug or integration to change the URL). Copy
                 button mirrors the Webhook Secret row's UX. -->
            <div>
              <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                Delivery URL
                <span class="ml-2 normal-case text-[10px] text-gray-400">
                  paste into {{ integ.label }} webhook configuration
                </span>
              </label>
              <div class="flex items-center gap-1">
                <input
                  type="text"
                  readonly
                  class="flex-1 font-mono text-xs border border-gray-300 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100/60 text-gray-700 dark:text-gray-200 rounded px-2 py-1.5 select-all cursor-text"
                  [value]="commerceDeliveryUrl(integ.key)" />
                <button type="button"
                        class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                        (click)="commerceCopyDeliveryUrl(integ.key)"
                        title="Copy delivery URL to clipboard">
                  <i class="fa-solid fa-copy"></i>
                </button>
              </div>
            </div>

            <!-- Webhook topics — exact strings to select on the upstream
                 side. Each topic maps to a separate webhook entry with
                 the SAME Delivery URL + Secret. Rendered only when the
                 integration has known topics (empty array = we haven't
                 confirmed the exact names yet). -->
            <div *ngIf="integ.webhook_topics?.length">
              <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                Webhook topics
                <span class="ml-2 normal-case text-[10px] text-gray-400">
                  create one webhook per topic in {{ integ.label }} — same Delivery URL + Secret for all
                </span>
              </label>
              <ul class="border border-gray-200 dark:border-[#434863] rounded bg-gray-50 dark:bg-app-black-100/60 divide-y divide-gray-200 dark:divide-[#434863]">
                <li *ngFor="let topic of integ.webhook_topics" class="flex items-center gap-2 px-2 py-1.5">
                  <i class="fa-solid fa-circle-dot text-[8px] text-gray-400"></i>
                  <code class="flex-1 font-mono text-xs text-gray-800 dark:text-gray-200 select-all">{{ topic }}</code>
                  <button type="button"
                          class="px-2 py-0.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-500 dark:text-gray-400 text-[11px]"
                          (click)="commerceCopyText(topic, 'Topic')"
                          title="Copy topic name">
                    <i class="fa-solid fa-copy"></i>
                  </button>
                </li>
              </ul>
            </div>

            <!-- Secret field with the 5 buttons. Label is per-provider
                 (Kartra: "IPN Secret key", Stripe: "Signing secret",
                 WC/SamCart: "Webhook secret") so admins can match it to
                 the field name on the upstream side without translating. -->
            <div>
              <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                {{ integ.secret_label }}
                <span *ngIf="commerceUi[integ.key].has_secret_stored" class="ml-2 normal-case text-[10px] text-green-600 dark:text-green-400">
                  <i class="fa-solid fa-circle-check"></i> stored
                </span>
                <span *ngIf="!commerceUi[integ.key].has_secret_stored" class="ml-2 normal-case text-[10px] text-gray-400">
                  <i class="fa-regular fa-circle"></i> not set
                </span>
              </label>
              <div class="flex items-center gap-1">
                <input
                  [type]="commerceUi[integ.key].secret_revealed ? 'text' : 'password'"
                  [placeholder]="commerceUi[integ.key].has_secret_stored && !commerceUi[integ.key].secret_field ? '••••••••••••••••' : ''"
                  class="flex-1 font-mono text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                  [(ngModel)]="commerceUi[integ.key].secret_field"
                  (keydown)="onSecretUserInput(integ.key, $event)"
                  (paste)="onSecretUserInput(integ.key)"
                  (cut)="onSecretUserInput(integ.key)"
                  autocomplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                  [attr.name]="'wc_secret_' + integ.key + '_no_autofill'"
                  [attr.id]="'wc_secret_' + integ.key + '_no_autofill'" />
                <button type="button"
                        class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                        (click)="commerceToggleReveal(integ.key)"
                        [title]="commerceUi[integ.key].secret_revealed ? 'Hide' : 'Reveal'">
                  <i class="fa-solid" [class.fa-eye]="!commerceUi[integ.key].secret_revealed" [class.fa-eye-slash]="commerceUi[integ.key].secret_revealed"></i>
                </button>
                <button type="button"
                        class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                        (click)="commerceCopy(integ.key)"
                        title="Copy to clipboard">
                  <i class="fa-solid fa-copy"></i>
                </button>
                <button type="button"
                        class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                        (click)="commercePaste(integ.key)"
                        title="Paste from clipboard">
                  <i class="fa-solid fa-paste"></i>
                </button>
                <button type="button"
                        class="px-2 py-1.5 border rounded text-xs font-semibold uppercase tracking-wide"
                        [ngClass]="commerceCanGenerate(integ.key)
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10'
                          : 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'"
                        [disabled]="!commerceCanGenerate(integ.key)"
                        (click)="commerceGenerate(integ.key)"
                        [title]="commerceCanGenerate(integ.key) ? 'Generate a 32-byte random secret' : 'Clear the field first'">
                  Generate
                </button>
                <button type="button"
                        class="px-2 py-1.5 border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-semibold uppercase tracking-wide"
                        (click)="commerceClear(integ.key)"
                        title="Clear the secret on both sides (server + field)">
                  Clear
                </button>
              </div>
              <div *ngIf="commerceUi[integ.key].secret_dirty" class="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                <i class="fa-solid fa-circle-info mr-1"></i>
                Unsaved change — click Save to persist this value.
              </div>
            </div>

            <!-- Ping handling -->
            <div>
              <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                Reachability ping log
                <span class="ml-2 normal-case text-[10px] text-gray-400">where {{ integ.label }} "delivery test" pings are recorded</span>
              </label>
              <select class="w-full text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                      [(ngModel)]="commerceUi[integ.key].ping_log">
                <option value="error_reports">Error Reports (default — visible in /admin/error-reports)</option>
                <option value="commerce_events">Commerce Events (audit row in /admin/commerce-events)</option>
                <option value="none">None (silent 200, no DB write)</option>
              </select>
            </div>

            <!-- Ping stats -->
            <div class="border border-gray-200 dark:border-[#434863] rounded p-3 bg-gray-50 dark:bg-app-black-100/40">
              <div class="flex items-center justify-between mb-2">
                <label class="block text-xs uppercase text-gray-500 dark:text-gray-400">
                  Ping stats
                </label>
                <button type="button"
                        class="text-[10px] uppercase tracking-wide border border-red-300 dark:border-red-700 rounded px-2 py-0.5 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-700 dark:text-red-400"
                        (click)="commerceResetPingStats(integ.key)"
                        title="Wipe most-recent + today/month/lifetime counts on the customer doc">
                  <i class="fa-regular fa-trash-can mr-1"></i>Reset
                </button>
              </div>
              <div class="grid grid-cols-4 gap-3 text-xs">
                <div>
                  <div class="text-[10px] uppercase text-gray-500 dark:text-gray-400">Most recent</div>
                  <div class="font-mono text-gray-900 dark:text-white cursor-help" [title]="formatFullPingWhen(commerceMostRecentPing[integ.key])">{{ formatPingRecency(commerceMostRecentPing[integ.key]) }}</div>
                </div>
                <div>
                  <div class="text-[10px] uppercase text-gray-500 dark:text-gray-400">Today (ET)</div>
                  <div class="font-mono text-gray-900 dark:text-white">{{ commercePingCounts[integ.key]?.today || 0 }}</div>
                </div>
                <div>
                  <div class="text-[10px] uppercase text-gray-500 dark:text-gray-400">This month (ET)</div>
                  <div class="font-mono text-gray-900 dark:text-white">{{ commercePingCounts[integ.key]?.month || 0 }}</div>
                </div>
                <div>
                  <div class="text-[10px] uppercase text-gray-500 dark:text-gray-400">Lifetime</div>
                  <div class="font-mono text-gray-900 dark:text-white">{{ commercePingCounts[integ.key]?.lifetime || 0 }}</div>
                </div>
              </div>
            </div>

              </div><!-- /webhook_section_open body -->
            </div><!-- /webhook setup collapsible -->

            <!-- Default scanner -->
            <div>
              <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                Default scanner
                <span class="ml-2 normal-case text-[10px] text-gray-400">used when a webhook line item is not in product_map</span>
              </label>
              <select class="w-full text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                      [(ngModel)]="commerceUi[integ.key].default_scanner">
                <option value="">— none —</option>
                <option [value]="COMMERCE_MEMBERS_PAGE">Members page (no specific scanner)</option>
                <option *ngFor="let s of commerceScannerOptions()" [value]="s.id" [ngStyle]="s.disabled ? {color: '#dc2626'} : null">{{ s.name }} &nbsp;({{ s.id }}){{ s.disabled ? ' — disabled' : '' }}</option>
                <!-- Surface a stored default that isn't in the customer's current scanner list
                     (drifted assignment OR an inherited slug) so the popup doesn't silently
                     swallow it on next save. Excludes the Members-page sentinel which is
                     always valid. -->
                <option *ngIf="commerceUi[integ.key].default_scanner &&
                               commerceUi[integ.key].default_scanner !== COMMERCE_MEMBERS_PAGE &&
                               !commerceScannerIdInCatalog(commerceUi[integ.key].default_scanner)"
                        [value]="commerceUi[integ.key].default_scanner">
                  {{ commerceUi[integ.key].default_scanner }} (not in customer's current scanner list)
                </option>
              </select>
              <div *ngIf="commerceScannerOptions().length === 0" class="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                <i class="fa-solid fa-circle-info mr-1"></i>
                This customer has no scanners assigned in F2-ADMIN.Scanners yet. Choose "Members page" to provision general customer access, or add a scanner row first.
              </div>
            </div>

            <!-- Product map — reusable expandable component -->
            <app-product-map-editor
              [(open)]="commerceProductMapOpen[integ.key]"
              [(rows)]="commerceProductMapRows[integ.key]"
              [scannerCatalog]="commerceScannerOptions()"
              [scannerDocs]="commerceScannerDocs"
              [customer]="commerceRecord"
              [productNames]="commerceProductNames[integ.key] || {}"
              [pastBuyerCountsProvider]="commercePastBuyerCountsProvider(integ.key)"
              [membersValue]="COMMERCE_MEMBERS_PAGE"
              [wildcardValue]="COMMERCE_WILDCARD"
              [title]="integ.label + ' product map'"
              subtitle="each upstream product id → what to grant"
              [keyLabel]="integ.key === 'woocommerce' ? 'WC product_id' : 'product_id'"
              targetLabel="Target"
              keyPlaceholder="e.g. 95">
            </app-product-map-editor>

            <!-- Integration guide(s) — live-rendered + any uploaded assets -->
            <div>
              <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                Integration guide
                <span class="ml-2 normal-case text-[10px] text-gray-400">delivery URL is slug-substituted per customer</span>
              </label>
              <ul class="space-y-1">
                <li *ngIf="liveGuideUrlFor(integ.key)" class="flex items-center gap-1">
                  <a [href]="liveGuideUrlFor(integ.key)" target="_blank" rel="noopener"
                     class="text-blue-600 dark:text-blue-400 hover:underline text-xs">
                    <i class="fa-solid fa-file-lines mr-1"></i>Live-rendered guide for this customer
                    <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[9px] opacity-70"></i>
                  </a>
                  <button type="button"
                          class="text-gray-500 dark:text-gray-400 hover:text-app-teal-100 text-[11px] px-1"
                          (click)="copyToClipboard(liveGuideUrlFor(integ.key))"
                          [title]="'Copy URL: ' + liveGuideUrlFor(integ.key)">
                    <i class="fa-regular fa-copy"></i>
                  </button>
                  <span class="ml-2 text-[10px] text-gray-400">renders with the correct customer slug substituted</span>
                </li>
                <li *ngFor="let a of commerceAssetsFor(integ.key)" class="flex items-center gap-1">
                  <a [href]="serveUrlForAsset(a)" target="_blank" rel="noopener"
                     class="text-blue-600 dark:text-blue-400 hover:underline text-xs">
                    <i class="fa-solid fa-file-arrow-down mr-1"></i>{{ a.label || a.filename }}
                    <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[9px] opacity-70"></i>
                  </a>
                  <button type="button"
                          class="text-gray-500 dark:text-gray-400 hover:text-app-teal-100 text-[11px] px-1"
                          (click)="copyToClipboard(serveUrlForAsset(a))"
                          [title]="'Copy URL: ' + serveUrlForAsset(a)">
                    <i class="fa-regular fa-copy"></i>
                  </button>
                  <span class="ml-2 text-[10px] text-gray-400">uploaded &middot; {{ a.content_type }} &middot; {{ a.size }} bytes</span>
                </li>
              </ul>
              <div *ngIf="commerceAssetsLoading" class="text-xs text-gray-500 dark:text-gray-400 italic mt-1">Loading uploaded assets…</div>
            </div>

            <!-- REST API tools (read-side reconcile) — expandable -->
            <div *ngIf="integ.key === 'woocommerce'" class="border border-gray-200 dark:border-[#434863] rounded">
              <button type="button"
                      class="w-full px-3 py-2 flex items-center justify-between text-left bg-gray-50 dark:bg-app-black-100/40 hover:bg-gray-100 dark:hover:bg-app-blue-100/30"
                      (click)="commerceUi[integ.key].api_section_open = !commerceUi[integ.key].api_section_open">
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  <i class="fa-solid fa-key mr-1"></i>WC REST API (optional)
                  <span class="ml-2 normal-case text-[10px] text-gray-400">read-only credentials for reconcile / download</span>
                </span>
                <i class="fa-solid" [class.fa-chevron-down]="!commerceUi[integ.key].api_section_open" [class.fa-chevron-up]="commerceUi[integ.key].api_section_open"></i>
              </button>
              <div *ngIf="commerceUi[integ.key].api_section_open" class="p-3 space-y-3">
                <div class="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                  Generate in WP Admin &rarr; <strong>WooCommerce &rarr; Settings &rarr; Advanced &rarr; REST API &rarr; Add key</strong>.
                  Permissions: <strong>Read</strong>. Paste the WP origin URL (no trailing path), the Consumer Key, and the Consumer Secret. F2 calls <code>/wp-json/wc/v3/subscriptions</code> with HTTP Basic auth.
                </div>

                <!-- API base URL -->
                <div>
                  <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">API base URL</label>
                  <input type="text"
                         class="w-full font-mono text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                         [(ngModel)]="commerceUi[integ.key].api_base_url"
                         placeholder="e.g. https://quantpivotpro.com" />
                </div>

                <!-- Consumer Key (hidden by default; eye to reveal) -->
                <div>
                  <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Consumer Key</label>
                  <div class="flex items-center gap-1">
                    <input
                      [type]="commerceUi[integ.key].consumer_key_revealed ? 'text' : 'password'"
                      class="flex-1 font-mono text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                      [(ngModel)]="commerceUi[integ.key].consumer_key"
                      placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      autocomplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                      [attr.name]="'wc_ck_' + integ.key + '_no_autofill'"
                      [attr.id]="'wc_ck_' + integ.key + '_no_autofill'" />
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="commerceConsumerKeyToggleReveal(integ.key)"
                            [title]="commerceUi[integ.key].consumer_key_revealed ? 'Hide' : 'Reveal'">
                      <i class="fa-solid" [class.fa-eye]="!commerceUi[integ.key].consumer_key_revealed" [class.fa-eye-slash]="commerceUi[integ.key].consumer_key_revealed"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="copyToClipboard(commerceUi[integ.key].consumer_key)"
                            title="Copy to clipboard">
                      <i class="fa-solid fa-copy"></i>
                    </button>
                  </div>
                </div>

                <!-- Consumer Secret (mirrors webhook-secret reveal pattern) -->
                <div>
                  <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                    Consumer Secret
                    <span *ngIf="commerceUi[integ.key].has_consumer_secret_stored" class="ml-2 normal-case text-[10px] text-green-600 dark:text-green-400">
                      <i class="fa-solid fa-circle-check"></i> stored
                    </span>
                    <span *ngIf="!commerceUi[integ.key].has_consumer_secret_stored" class="ml-2 normal-case text-[10px] text-gray-400">
                      <i class="fa-regular fa-circle"></i> not set
                    </span>
                  </label>
                  <div class="flex items-center gap-1">
                    <input
                      [type]="commerceUi[integ.key].consumer_secret_revealed ? 'text' : 'password'"
                      [placeholder]="commerceUi[integ.key].has_consumer_secret_stored && !commerceUi[integ.key].consumer_secret_field ? '••••••••••••••••' : 'cs_xxxxxxxxxxxx…'"
                      class="flex-1 font-mono text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                      [(ngModel)]="commerceUi[integ.key].consumer_secret_field"
                      (keydown)="onConsumerSecretUserInput(integ.key, $event)"
                      (paste)="onConsumerSecretUserInput(integ.key)"
                      (cut)="onConsumerSecretUserInput(integ.key)"
                      autocomplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                      [attr.name]="'wc_cs_' + integ.key + '_no_autofill'"
                      [attr.id]="'wc_cs_' + integ.key + '_no_autofill'" />
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="commerceConsumerSecretToggleReveal(integ.key)"
                            [title]="commerceUi[integ.key].consumer_secret_revealed ? 'Hide' : 'Reveal'">
                      <i class="fa-solid" [class.fa-eye]="!commerceUi[integ.key].consumer_secret_revealed" [class.fa-eye-slash]="commerceUi[integ.key].consumer_secret_revealed"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="commerceConsumerSecretCopy(integ.key)"
                            title="Copy to clipboard">
                      <i class="fa-solid fa-copy"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="commerceConsumerSecretPaste(integ.key)"
                            title="Paste from clipboard">
                      <i class="fa-solid fa-paste"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-semibold uppercase tracking-wide"
                            (click)="commerceConsumerSecretClear(integ.key)"
                            title="Clear the secret (save to persist)">
                      Clear
                    </button>
                  </div>
                  <div *ngIf="commerceUi[integ.key].consumer_secret_dirty" class="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                    <i class="fa-solid fa-circle-info mr-1"></i>
                    Unsaved change — click Save to persist this value.
                  </div>
                </div>

                <!-- Reconcile + Download JSON -->
                <div class="flex flex-col gap-2 pt-1">
                  <div class="flex items-center justify-between">
                    <div class="text-[11px] text-gray-500 dark:text-gray-400 leading-tight pr-2">
                      <strong>Reconcile / Sync</strong> &mdash; for each WC subscription: creates missing F2 users, adds scanners for active subs, removes scanners for non-active subs, adds the customer slug to existing users that don't have it. Every action lands in /admin/commerce-events with sub_id + start/stop dates + reason for billing replay.
                    </div>
                    <button type="button"
                            class="px-3 py-1 rounded border border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 text-xs uppercase tracking-wide whitespace-nowrap"
                            (click)="commerceReconcileWcSubscriptions(integ.key)">
                      <i class="fa-solid fa-arrows-rotate mr-1"></i>Reconcile WC &rarr; F2
                    </button>
                  </div>
                  <div class="flex items-center justify-between">
                    <div class="text-[11px] text-gray-500 dark:text-gray-400 pr-2">
                      <strong>Download JSON</strong> &mdash; paginates per_page=100 across every page (max 200). Output is a JSON file with every subscription regardless of status.
                    </div>
                    <button type="button"
                            [disabled]="commerceUi[integ.key]?.downloadingSubscriptions"
                            class="px-3 py-1 rounded border border-emerald-500 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 disabled:opacity-60 disabled:cursor-not-allowed text-xs uppercase tracking-wide whitespace-nowrap"
                            (click)="commerceDownloadWcSubscriptions(integ.key)">
                      <ng-container *ngIf="!commerceUi[integ.key]?.downloadingSubscriptions; else wcDlSpin">
                        <i class="fa-solid fa-download mr-1"></i>Download subscriptions JSON
                      </ng-container>
                      <ng-template #wcDlSpin>
                        <i class="fa-solid fa-spinner fa-spin mr-1"></i>Downloading…
                      </ng-template>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- SamCart REST API tools (read-side reconcile) — expandable -->
            <div *ngIf="integ.key === 'samcart'" class="border border-gray-200 dark:border-[#434863] rounded">
              <button type="button"
                      class="w-full px-3 py-2 flex items-center justify-between text-left bg-gray-50 dark:bg-app-black-100/40 hover:bg-gray-100 dark:hover:bg-app-blue-100/30"
                      (click)="commerceUi[integ.key].api_section_open = !commerceUi[integ.key].api_section_open">
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  <i class="fa-solid fa-key mr-1"></i>SamCart REST API (optional)
                  <span class="ml-2 normal-case text-[10px] text-gray-400">api token for reconcile / download</span>
                </span>
                <i class="fa-solid" [class.fa-chevron-down]="!commerceUi[integ.key].api_section_open" [class.fa-chevron-up]="commerceUi[integ.key].api_section_open"></i>
              </button>
              <div *ngIf="commerceUi[integ.key].api_section_open" class="p-3 space-y-3">
                <div class="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                  Generate in SamCart &rarr; <strong>Settings &rarr; API &rarr; Generate Token</strong>. F2 calls <code>https://api.samcart.com/v1/subscriptions</code> with <code>Authorization: Bearer &lt;token&gt;</code>. The token is stored on the Customer doc and re-used by both reconcile and history download.
                </div>

                <!-- API Token (reuses the WC consumer_key slot so the editor is shared).
                     IT-F2-432 c/ac99194c + f2-session clar bd68eb62: changed the
                     [type] binding to always 'text' so Chrome/Edge don't apply
                     their password-field autofill overlay path, which was
                     intercepting Mike's paste + leaving the ngModel binding
                     stale. The eye toggle now flips between font-mono and
                     a CSS blur-filter mask instead of swapping the input type.
                     Added (ngModelChange) for explicit value capture +
                     consumer_key_dirty flag so operators get an "unsaved"
                     indicator before clicking Save (parity with the other
                     dirty-tracked fields in this popup). -->
                <div>
                  <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                    API Token
                    <span *ngIf="commerceUi[integ.key].consumer_key?.length"
                          class="normal-case ml-2 text-[10px] text-gray-500 dark:text-gray-400">
                      {{ commerceUi[integ.key].consumer_key?.length || 0 }} chars
                    </span>
                    <span *ngIf="commerceUi[integ.key].consumer_key_dirty"
                          class="normal-case ml-2 text-[10px] text-amber-500 dark:text-amber-400">
                      <i class="fa-solid fa-circle-info mr-1"></i>unsaved
                    </span>
                  </label>
                  <div class="flex items-center gap-1">
                    <input type="text"
                      class="flex-1 font-mono text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                      [class.sc-token-masked]="!commerceUi[integ.key].consumer_key_revealed"
                      [ngModel]="commerceUi[integ.key].consumer_key"
                      (ngModelChange)="onSamcartTokenChange(integ.key, $event)"
                      placeholder="sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      autocomplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                      [attr.name]="'sc_token_' + integ.key + '_no_autofill'"
                      [attr.id]="'sc_token_' + integ.key + '_no_autofill'" />
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="commerceConsumerKeyToggleReveal(integ.key)"
                            [title]="commerceUi[integ.key].consumer_key_revealed ? 'Hide' : 'Reveal'">
                      <i class="fa-solid" [class.fa-eye]="!commerceUi[integ.key].consumer_key_revealed" [class.fa-eye-slash]="commerceUi[integ.key].consumer_key_revealed"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="copyToClipboard(commerceUi[integ.key].consumer_key)"
                            title="Copy to clipboard">
                      <i class="fa-solid fa-copy"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-emerald-500 text-emerald-600 dark:text-emerald-400 rounded hover:bg-emerald-50 dark:hover:bg-emerald-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                            [disabled]="!commerceUi[integ.key].consumer_key || commerceUi[integ.key].testingSamcartToken"
                            (click)="commerceTestSamcartToken(integ.key)"
                            title="Probe SamCart API with the current token (saves nothing)">
                      <i *ngIf="!commerceUi[integ.key].testingSamcartToken" class="fa-solid fa-vial"></i>
                      <i *ngIf="commerceUi[integ.key].testingSamcartToken" class="fa-solid fa-spinner fa-spin"></i>
                    </button>
                  </div>
                  <div *ngIf="commerceUi[integ.key].samcartTokenTestResult"
                       class="mt-1 text-[11px] font-mono"
                       [class.text-emerald-500]="commerceUi[integ.key].samcartTokenTestResult!.ok"
                       [class.text-red-500]="!commerceUi[integ.key].samcartTokenTestResult!.ok">
                    <i class="fa-solid mr-1"
                       [class.fa-circle-check]="commerceUi[integ.key].samcartTokenTestResult!.ok"
                       [class.fa-circle-xmark]="!commerceUi[integ.key].samcartTokenTestResult!.ok"></i>
                    {{ commerceUi[integ.key].samcartTokenTestResult!.message }}
                  </div>
                </div>

              </div>
            </div>

            <!-- SamCart read-side actions. Reconcile (webhook-log replay)
                 stays visible as the ad-hoc path; the new Historical
                 Snapshot picker is the single source of truth for both
                 download and (next PR) dry-run reconciliation. Mike
                 IT-F2-198 2026-11-03 cid a73ce3bf. -->
            <div *ngIf="integ.key === 'samcart' && !!commerceUi[integ.key]?.consumer_key"
                 class="border border-gray-200 dark:border-[#434863] rounded">
              <div class="p-3 space-y-3">
                <div class="flex items-center justify-between">
                  <div class="text-[11px] text-gray-500 dark:text-gray-400 leading-tight pr-2">
                    <strong>Reconcile / Sync</strong> &mdash; webhook-log replay. For each SamCart subscription: creates missing F2 users, adds scanners for active subs, removes scanners for non-active subs. Every action lands in /admin/commerce-events for billing replay.
                  </div>
                  <button type="button"
                          class="px-3 py-1 rounded border border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 text-xs uppercase tracking-wide whitespace-nowrap"
                          (click)="commerceReconcileSamcartSubscriptions(integ.key)">
                    <i class="fa-solid fa-arrows-rotate mr-1"></i>Reconcile SamCart &rarr; F2
                  </button>
                </div>
              </div>
            </div>

            <!-- Historical Snapshots (IT-F2-198 cid a73ce3bf). Lists every
                 persisted snapshot from F2-ADMIN.SamCartSnapshots. Each row
                 saves to .json (opt-in ?include=raw) and (next PR) will feed
                 into the dry-run reconcile viewer. Trigger button at top-
                 right kicks off a fresh full sweep via sync-historical. -->
            <div *ngIf="integ.key === 'samcart' && !!commerceUi[integ.key]?.consumer_key"
                 class="border border-gray-200 dark:border-[#434863] rounded">
              <div class="px-3 py-2 flex items-center justify-between bg-gray-50 dark:bg-app-black-100/40 border-b border-gray-200 dark:border-[#434863] rounded-t">
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  <i class="fa-solid fa-camera mr-1"></i>Historical Snapshots
                  <span class="ml-2 normal-case text-[10px] text-gray-400">full subs + orders sweep via <code>pagination.next</code> cursor</span>
                </span>
                <div class="flex items-center gap-1">
                  <button type="button"
                          class="px-2 py-1 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-[11px] text-gray-700 dark:text-gray-300"
                          (click)="loadSamcartSnapshots(integ.key, { force: true })"
                          [disabled]="samcartSnapshotsLoading"
                          title="Reload snapshot list from the server">
                    <i class="fa-solid fa-arrow-rotate-right"
                       [class.fa-spin]="samcartSnapshotsLoading"></i>
                  </button>
                  <button type="button"
                          class="px-3 py-1 rounded border border-emerald-500 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 disabled:opacity-60 disabled:cursor-not-allowed text-xs uppercase tracking-wide whitespace-nowrap"
                          (click)="triggerSamcartSync(integ.key)"
                          [disabled]="!!samcartActiveSyncRunId"
                          [title]="samcartActiveSyncRunId ? 'A snapshot sync is already in progress; watch the row at the top of the list.' : 'Run a new full subs + orders sweep via pagination.next cursor. Large customers take a few minutes; progress ticks on the pending row.'">
                    <i *ngIf="!samcartActiveSyncRunId" class="fa-solid fa-cloud-arrow-down mr-1"></i>
                    <i *ngIf="samcartActiveSyncRunId" class="fa-solid fa-spinner fa-spin mr-1"></i>
                    <ng-container *ngIf="!samcartActiveSyncRunId">Run new snapshot</ng-container>
                    <ng-container *ngIf="samcartActiveSyncRunId">Syncing&hellip;</ng-container>
                  </button>
                </div>
              </div>

              <div class="p-3 space-y-2">
                <!-- Empty + loading state -->
                <div *ngIf="samcartSnapshotsLoading && samcartSnapshots.length === 0"
                     class="text-[11px] text-gray-500 dark:text-gray-400 italic">
                  <i class="fa-solid fa-spinner fa-spin mr-1"></i>Loading snapshots&hellip;
                </div>
                <div *ngIf="!samcartSnapshotsLoading && samcartSnapshots.length === 0"
                     class="text-[11px] text-gray-500 dark:text-gray-400 italic">
                  No snapshots yet. Click <strong>Run new snapshot</strong> to pull every subscription + order from SamCart. The first run on a large customer can take 5 minutes.
                </div>

                <!-- Snapshot list -->
                <div *ngFor="let snap of samcartSnapshots; trackBy: trackByRunId"
                     class="border border-gray-200 dark:border-[#434863] rounded px-3 py-2 bg-gray-50 dark:bg-app-black-100">
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 flex-wrap">
                        <code class="text-[11px] text-gray-500 dark:text-gray-400">{{ snap.run_id.slice(0, 23) }}</code>
                        <span *ngIf="snap.status === 'complete'"
                              class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">complete</span>
                        <span *ngIf="snap.status === 'pending'"
                              class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300">pending</span>
                        <span *ngIf="snap.status === 'failed'"
                              class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300">failed</span>
                        <span class="text-[11px] text-gray-500 dark:text-gray-400">
                          {{ samcartSnapshotRecency(snap.completed_at || snap.started_at) }}
                        </span>
                      </div>
                      <div class="mt-1 text-[11px] text-gray-700 dark:text-gray-300 font-mono">
                        <span>{{ snap.purchases_total || 0 }}</span>
                        <span class="text-gray-400">&nbsp;purchases&nbsp;=&nbsp;</span>
                        <span>{{ snap.subs_total || 0 }}</span>
                        <span class="text-gray-400">&nbsp;subs&nbsp;+&nbsp;</span>
                        <span>{{ snap.orders_total || 0 }}</span>
                        <span class="text-gray-400">&nbsp;orders&nbsp;&middot;&nbsp;</span>
                        <span>{{ snap.customer_ids_total || 0 }}</span>
                        <span class="text-gray-400">&nbsp;customers (</span><span>{{ snap.emails_resolved || 0 }}</span><span class="text-gray-400">&nbsp;emails resolved)</span>
                      </div>
                      <div *ngIf="(snap.new_product_ids_added || []).length"
                           class="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                        <i class="fa-solid fa-plus-circle mr-1"></i>
                        {{ snap.new_product_ids_added.length }} new product_id(s) auto-upserted into product_map as <code>unassigned</code>
                      </div>
                      <div *ngIf="(snap.errors || []).length"
                           class="mt-1 text-[11px] text-red-500 dark:text-red-400">
                        <i class="fa-solid fa-circle-exclamation mr-1"></i>
                        {{ snap.errors.length }} non-fatal sweep error(s)
                      </div>
                    </div>
                    <div class="flex items-center gap-1 flex-shrink-0">
                      <button type="button"
                              class="px-3 py-1 rounded border border-emerald-500 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 disabled:opacity-60 disabled:cursor-not-allowed text-xs uppercase tracking-wide whitespace-nowrap"
                              [disabled]="snap.status !== 'complete' || !!samcartSnapshotRowBusy[snap.run_id]"
                              (click)="saveSamcartSnapshot(snap.run_id, samcartSaveIncludeRawPref)"
                              [title]="snap.status !== 'complete' ? 'Only complete snapshots can be downloaded' : 'Save purchases[] as .json'">
                        <i *ngIf="samcartSnapshotRowBusy[snap.run_id] !== 'save' && samcartSnapshotRowBusy[snap.run_id] !== 'save-raw'"
                           class="fa-solid fa-floppy-disk mr-1"></i>
                        <i *ngIf="samcartSnapshotRowBusy[snap.run_id] === 'save' || samcartSnapshotRowBusy[snap.run_id] === 'save-raw'"
                           class="fa-solid fa-spinner fa-spin mr-1"></i>
                        Save .json
                      </button>
                    </div>
                  </div>
                </div>

                <!-- include=raw toggle. Default off; sticky across clicks. -->
                <label *ngIf="samcartSnapshots.length"
                       class="flex items-center gap-2 cursor-pointer pt-1 pl-1">
                  <input type="checkbox" class="rounded" [(ngModel)]="samcartSaveIncludeRawPref" />
                  <span class="text-[11px] text-gray-600 dark:text-gray-300">
                    Include raw subs + orders payload (<code>?include=raw</code>)
                    <span class="text-gray-400 dark:text-gray-500">operator audit mode &mdash; larger file, exact SamCart API responses</span>
                  </span>
                </label>
              </div>
            </div>

            <!-- Kartra API tools — expandable section with 3 credentials -->
            <div *ngIf="integ.key === 'kartra'" class="border border-gray-200 dark:border-[#434863] rounded">
              <button type="button"
                      class="w-full px-3 py-2 flex items-center justify-between text-left bg-gray-50 dark:bg-app-black-100/40 hover:bg-gray-100 dark:hover:bg-app-blue-100/30"
                      (click)="commerceUi[integ.key].api_section_open = !commerceUi[integ.key].api_section_open">
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  <i class="fa-solid fa-key mr-1"></i>Kartra API (optional)
                  <span class="ml-2 normal-case text-[10px] text-gray-400">app_id + api_key + api_password — for outbound calls to Kartra</span>
                </span>
                <i class="fa-solid" [class.fa-chevron-down]="!commerceUi[integ.key].api_section_open" [class.fa-chevron-up]="commerceUi[integ.key].api_section_open"></i>
              </button>
              <div *ngIf="commerceUi[integ.key].api_section_open && commerceUi[integ.key].api_credentials" class="p-3 space-y-3">
                <div class="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                  Generate in Kartra &rarr; <strong>Settings &rarr; Integrations &rarr; My Apps</strong>. Each app carries an <strong>App ID</strong>, an <strong>API Key</strong>, and an <strong>API Password</strong> — all three are required to call Kartra's API. F2 POSTs form-urlencoded to <code>https://app.kartra.com/api</code> with these values in the body plus command-specific fields.
                </div>

                <!-- App ID (reveal / copy / paste / clear pattern) -->
                <div>
                  <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">App ID</label>
                  <div class="flex items-center gap-1">
                    <input
                      [type]="commerceUi[integ.key].api_credentials!.app_id_revealed ? 'text' : 'password'"
                      class="flex-1 font-mono text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                      [(ngModel)]="commerceUi[integ.key].api_credentials!.app_id"
                      (input)="commerceUi[integ.key].api_credentials!.app_id_dirty = true"
                      placeholder="e.g. wVtlsPnvxOjW"
                      autocomplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other" />
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="kartraCredToggleReveal(integ.key, 'app_id')"
                            [title]="commerceUi[integ.key].api_credentials!.app_id_revealed ? 'Hide' : 'Reveal'">
                      <i class="fa-solid" [class.fa-eye]="!commerceUi[integ.key].api_credentials!.app_id_revealed" [class.fa-eye-slash]="commerceUi[integ.key].api_credentials!.app_id_revealed"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="copyToClipboard(commerceUi[integ.key].api_credentials!.app_id)"
                            title="Copy to clipboard">
                      <i class="fa-solid fa-copy"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="kartraCredPaste(integ.key, 'app_id')"
                            title="Paste from clipboard">
                      <i class="fa-solid fa-paste"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-semibold uppercase tracking-wide"
                            (click)="kartraCredClear(integ.key, 'app_id')"
                            title="Clear the App ID (save to persist)">
                      Clear
                    </button>
                  </div>
                  <div *ngIf="commerceUi[integ.key].api_credentials!.app_id_dirty" class="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                    <i class="fa-solid fa-circle-info mr-1"></i>Unsaved change — click Save to persist.
                  </div>
                </div>

                <!-- API Key (reveal / copy / paste / clear pattern) -->
                <div>
                  <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">API Key</label>
                  <div class="flex items-center gap-1">
                    <input
                      [type]="commerceUi[integ.key].api_credentials!.api_key_revealed ? 'text' : 'password'"
                      class="flex-1 font-mono text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                      [(ngModel)]="commerceUi[integ.key].api_credentials!.api_key"
                      (input)="commerceUi[integ.key].api_credentials!.api_key_dirty = true"
                      placeholder="e.g. IAxNhvOW"
                      autocomplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other" />
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="kartraCredToggleReveal(integ.key, 'api_key')"
                            [title]="commerceUi[integ.key].api_credentials!.api_key_revealed ? 'Hide' : 'Reveal'">
                      <i class="fa-solid" [class.fa-eye]="!commerceUi[integ.key].api_credentials!.api_key_revealed" [class.fa-eye-slash]="commerceUi[integ.key].api_credentials!.api_key_revealed"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="copyToClipboard(commerceUi[integ.key].api_credentials!.api_key)"
                            title="Copy to clipboard">
                      <i class="fa-solid fa-copy"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="kartraCredPaste(integ.key, 'api_key')"
                            title="Paste from clipboard">
                      <i class="fa-solid fa-paste"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-semibold uppercase tracking-wide"
                            (click)="kartraCredClear(integ.key, 'api_key')"
                            title="Clear the API Key (save to persist)">
                      Clear
                    </button>
                  </div>
                  <div *ngIf="commerceUi[integ.key].api_credentials!.api_key_dirty" class="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                    <i class="fa-solid fa-circle-info mr-1"></i>Unsaved change — click Save to persist.
                  </div>
                </div>

                <!-- API Password (secret — mirrors webhook_secret / consumer_secret pattern) -->
                <div>
                  <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                    API Password
                    <span *ngIf="commerceUi[integ.key].api_credentials!.has_api_password_stored" class="ml-2 normal-case text-[10px] text-green-600 dark:text-green-400">
                      <i class="fa-solid fa-circle-check"></i> stored
                    </span>
                    <span *ngIf="!commerceUi[integ.key].api_credentials!.has_api_password_stored" class="ml-2 normal-case text-[10px] text-gray-400">
                      <i class="fa-regular fa-circle"></i> not set
                    </span>
                  </label>
                  <div class="flex items-center gap-1">
                    <input
                      [type]="commerceUi[integ.key].api_credentials!.api_password_revealed ? 'text' : 'password'"
                      [placeholder]="commerceUi[integ.key].api_credentials!.has_api_password_stored && !commerceUi[integ.key].api_credentials!.api_password_field ? '••••••••••••••••' : 'e.g. KlXkTLsZqngp'"
                      class="flex-1 font-mono text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1.5"
                      [(ngModel)]="commerceUi[integ.key].api_credentials!.api_password_field"
                      (input)="commerceUi[integ.key].api_credentials!.api_password_dirty = true"
                      autocomplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other" />
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="kartraCredToggleReveal(integ.key, 'api_password')"
                            [title]="commerceUi[integ.key].api_credentials!.api_password_revealed ? 'Hide' : 'Reveal'">
                      <i class="fa-solid" [class.fa-eye]="!commerceUi[integ.key].api_credentials!.api_password_revealed" [class.fa-eye-slash]="commerceUi[integ.key].api_credentials!.api_password_revealed"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="copyToClipboard(commerceUi[integ.key].api_credentials!.api_password_field)"
                            [disabled]="!commerceUi[integ.key].api_credentials!.api_password_field"
                            title="Copy to clipboard (only when currently typed value present)">
                      <i class="fa-solid fa-copy"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-700 dark:text-gray-300"
                            (click)="kartraCredPaste(integ.key, 'api_password')"
                            title="Paste from clipboard">
                      <i class="fa-solid fa-paste"></i>
                    </button>
                    <button type="button"
                            class="px-2 py-1.5 border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-semibold uppercase tracking-wide"
                            (click)="kartraCredClear(integ.key, 'api_password')"
                            title="Clear the API Password (save to persist)">
                      Clear
                    </button>
                  </div>
                  <div *ngIf="commerceUi[integ.key].api_credentials!.api_password_dirty" class="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                    <i class="fa-solid fa-circle-info mr-1"></i>Unsaved change — click Save to persist.
                  </div>
                </div>

                <!-- API design note — Kartra's per-lead constraint -->
                <div class="mt-2 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed border-t border-gray-200 dark:border-[#434863] pt-3">
                  <i class="fa-solid fa-triangle-exclamation text-amber-500 mr-1"></i>
                  <strong>Kartra's API is per-lead</strong> — every command needs a target <code>lead[email]</code> or <code>lead[id]</code>. There is no "list all leads" endpoint. F2's <code>reconcile-from-emails</code> endpoint takes a list of emails (from Kartra Leads CSV export, F2's own user list, or wherever) and calls <code>retrieve_subscriptions_from_lead</code> for each, filtered to Active. Optionally hydrates each with <code>get_subscription_details</code> to surface <code>subscription_product_id</code> for product-map inference.
                </div>
              </div>
            </div>

            <!-- Per-integration Save + Revert -->
            <div class="flex justify-end gap-2">
              <button class="px-3 py-1 rounded border text-xs uppercase tracking-wide"
                      [ngClass]="commerceIsDirty(integ.key)
                        ? 'border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10'
                        : 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'"
                      [disabled]="!commerceIsDirty(integ.key)"
                      (click)="commerceRevert(integ.key)"
                      [title]="commerceIsDirty(integ.key) ? 'Discard unsaved changes for ' + integ.label : 'No unsaved changes'">
                Revert
              </button>
              <button class="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-xs uppercase tracking-wide"
                      (click)="commerceSave(integ.key)">
                Save {{ integ.label }}
                <span *ngIf="commerceIsDirty(integ.key)" class="ml-1 text-amber-200">●</span>
              </button>
            </div>
          </div>

          <!-- "Coming soon" body for not-ready integrations -->
          <div *ngIf="!integ.ready" class="p-4 text-xs text-gray-500 dark:text-gray-400">
            {{ integ.label }} integration is on the roadmap. The popup will show enable / secret / default-scanner controls here once the inbound adapter ships.
          </div>
        </div>
      </ng-container>

      <!-- Email Provider section (sibling to per-integration cards).
           One config per customer (NOT per integration). Triggers off
           commerce activations to add/remove the user from a Customer.io
           segment + flip an attribute. Collapsed by default. -->
      <div *ngIf="commerceEmailUi" class="border border-gray-200 dark:border-[#434863] rounded">
        <button type="button"
                class="w-full px-4 py-2 bg-gray-50 dark:bg-app-black-100 flex items-center justify-between rounded-t hover:bg-gray-100 dark:hover:bg-app-blue-100/50"
                (click)="commerceEmailSectionOpen = !commerceEmailSectionOpen">
          <div class="flex items-center gap-3">
            <h4 class="font-semibold text-gray-900 dark:text-white">Email Provider</h4>
            <span *ngIf="commerceEmailUi.enabled" class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">Enabled</span>
            <span *ngIf="!commerceEmailUi.enabled" class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Disabled</span>
            <span class="text-[11px] text-gray-500 dark:text-gray-400">{{ commerceEmailUi.kind || 'customer_io' }}</span>
            <span *ngIf="commerceEmailIsDirty()" class="text-[11px] text-amber-600 dark:text-amber-400">●</span>
          </div>
          <i class="fa-solid" [class.fa-chevron-down]="!commerceEmailSectionOpen" [class.fa-chevron-up]="commerceEmailSectionOpen"></i>
        </button>

        <div *ngIf="commerceEmailSectionOpen" class="p-4 space-y-4">
          <div class="text-[11px] text-gray-500 dark:text-gray-400">
            When a commerce activation / revoke fires for one of the trigger scanners below,
            the user is added to (or removed from) the segment and the attribute is flipped.
            All credentials live on this customer doc — no env hardcoding.
          </div>

          <label class="inline-flex items-center gap-2">
            <input type="checkbox" class="rounded" [(ngModel)]="commerceEmailUi.enabled" />
            <span class="text-xs text-gray-700 dark:text-gray-300">Enable email provider sync</span>
          </label>

          <div *ngIf="commerceEmailUi.enabled" class="space-y-3">
            <div>
              <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                Alerts delivery backend
                <span class="ml-2 normal-case text-[10px] text-gray-400">where the scheduled + on-demand alert emails ship from</span>
              </label>
              <select class="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-blue-100/40 text-gray-900 dark:text-white"
                      [(ngModel)]="commerceEmailUi.send_via">
                <option value="ses">SES — direct via pool identity (recommended, autonomous)</option>
                <option value="customer_io">Customer.io — legacy campaign webhook</option>
              </select>
              <div *ngIf="commerceEmailUi.send_via === 'ses'" class="mt-1 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                Subscribership resolved from Cognito <code>custom:scanners</code>. Cognito is source of truth; unsubscribe removes the scanner slug from that field. Customer.io credentials below are ignored in this mode.
              </div>
              <div *ngIf="commerceEmailUi.send_via === 'customer_io'" class="mt-1 text-[11px] text-amber-600 dark:text-amber-400 leading-relaxed">
                Requires the Customer.io Track API credentials below (site_id + api_key + segment_id + attribute_name). WC/SamCart webhooks call CIO identify + segment add/remove on every entitlement change.
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Provider</label>
                <select class="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-blue-100/40 text-gray-900 dark:text-white"
                        [(ngModel)]="commerceEmailUi.kind">
                  <option value="customer_io">Customer.io</option>
                </select>
              </div>
              <div>
                <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Site ID</label>
                <input type="text" class="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-blue-100/40 text-gray-900 dark:text-white"
                       [(ngModel)]="commerceEmailUi.site_id"
                       placeholder="customer.io site id" />
              </div>
              <div>
                <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Segment ID</label>
                <input type="text" class="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-blue-100/40 text-gray-900 dark:text-white"
                       [(ngModel)]="commerceEmailUi.segment_id"
                       placeholder="numeric segment id" />
              </div>
              <div>
                <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                  Attribute name
                  <span class="ml-2 normal-case text-[10px] text-gray-400">flipped true on grant, false on revoke</span>
                </label>
                <input type="text" class="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-blue-100/40 text-gray-900 dark:text-white"
                       [(ngModel)]="commerceEmailUi.attribute_name"
                       placeholder="e.g. qpp_algo_active" />
              </div>
            </div>

            <div>
              <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                API key
                <span *ngIf="commerceEmailUi.has_api_key_stored" class="ml-2 normal-case text-[10px] text-green-600 dark:text-green-400">
                  <i class="fa-solid fa-check-circle"></i> stored
                </span>
                <span *ngIf="!commerceEmailUi.has_api_key_stored" class="ml-2 normal-case text-[10px] text-gray-400">
                  not set
                </span>
              </label>
              <div class="flex gap-2 items-stretch">
                <input class="flex-1 px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-blue-100/40 text-gray-900 dark:text-white"
                       [type]="commerceEmailUi.api_key_revealed ? 'text' : 'password'"
                       [placeholder]="commerceEmailUi.has_api_key_stored && !commerceEmailUi.api_key_field ? '••••••••••••••••' : 'paste api key'"
                       autocomplete="off"
                       [(ngModel)]="commerceEmailUi.api_key_field"
                       (keydown)="onEmailApiKeyUserInput($event)"
                       (paste)="onEmailApiKeyUserInput()"
                       (cut)="onEmailApiKeyUserInput()" />
                <button type="button"
                        class="px-2 py-1.5 rounded border border-gray-300 dark:border-[#434863] hover:bg-gray-100 dark:hover:bg-app-blue-100/50"
                        (click)="commerceEmailToggleReveal()"
                        [title]="commerceEmailUi.api_key_revealed ? 'Hide' : 'Reveal'">
                  <i class="fa-solid" [class.fa-eye]="!commerceEmailUi.api_key_revealed" [class.fa-eye-slash]="commerceEmailUi.api_key_revealed"></i>
                </button>
                <button type="button"
                        class="px-2 py-1.5 rounded border border-gray-300 dark:border-[#434863] hover:bg-gray-100 dark:hover:bg-app-blue-100/50"
                        (click)="commerceEmailCopyApiKey()"
                        title="Copy">
                  <i class="fa-solid fa-copy"></i>
                </button>
                <button type="button"
                        class="px-2 py-1.5 rounded border border-gray-300 dark:border-[#434863] hover:bg-gray-100 dark:hover:bg-app-blue-100/50"
                        (click)="commerceEmailPasteApiKey()"
                        title="Paste">
                  <i class="fa-solid fa-paste"></i>
                </button>
                <button type="button"
                        class="px-2 py-1.5 rounded border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
                        (click)="commerceEmailClearApiKey()"
                        title="Clear">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
              <div *ngIf="commerceEmailUi.api_key_dirty" class="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                Unsaved API key change
              </div>
            </div>

            <div>
              <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">
                Trigger scanner slugs
                <span class="ml-2 normal-case text-[10px] text-gray-400">comma-separated F2-ADMIN.Scanners._id values</span>
              </label>
              <input type="text" class="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-blue-100/40 text-gray-900 dark:text-white"
                     [(ngModel)]="commerceEmailUi.triggers_text"
                     placeholder="quant-pivot-pro, qpp-algo-alerts" />
            </div>

          </div>

          <div class="flex justify-end gap-2">
            <button class="px-3 py-1 rounded border text-xs uppercase tracking-wide"
                    [ngClass]="commerceEmailIsDirty()
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10'
                      : 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'"
                    [disabled]="!commerceEmailIsDirty()"
                    (click)="commerceEmailRevert()"
                    [title]="commerceEmailIsDirty() ? 'Discard unsaved email provider changes' : 'No unsaved changes'">
              Revert
            </button>
            <button class="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-xs uppercase tracking-wide"
                    (click)="commerceEmailSave()">
              Save Email Provider
              <span *ngIf="commerceEmailIsDirty()" class="ml-1 text-amber-200">●</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Cross-integration commerce settings. Applies to every commerce
           webhook path (WC / Stripe / Kartra / SamCart / partner API) for
           this customer, not per-integration. -->
      <div *ngIf="commerceSharedUi" class="border border-gray-200 dark:border-[#434863] rounded">
        <div class="px-4 py-2 bg-gray-50 dark:bg-app-black-100 rounded-t">
          <h4 class="font-semibold text-gray-900 dark:text-white">
            <i class="fa-solid fa-gears mr-2 text-gray-500 dark:text-gray-400"></i>
            Shared commerce settings
          </h4>
          <div class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            Applies to every commerce integration above (and the partner-API activate/deactivate path).
          </div>
        </div>
        <div class="p-4 space-y-3">
          <label class="inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" class="rounded mt-0.5" [(ngModel)]="commerceSharedUi.welcome_email_enabled" />
            <span class="text-xs text-gray-800 dark:text-gray-200 leading-relaxed">
              Send F2-branded welcome email on commerce activation
              <span class="block mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                When ON, F2 sends a welcome email from this customer's SES identity every time a commerce
                webhook activates a subscriber. New users (or FORCE_CHANGE_PASSWORD) get a magic-link invite
                landing on the customer's own domain. Existing users get a plain "your subscription is active"
                note pointing to their granted scanner's URL.
                <br />
                <b>Turn ON</b> for customers whose users interact with F2's own login/member surface directly
                (customer-hosted portal).
                <b>Leave OFF</b> for SSO/OEM tenants (MTA, TT, TL, Simpler, OP) whose users never see F2 —
                a welcome from us would confuse them.
                When ON, Cognito's own temp-password email (below) is suppressed regardless of that toggle.
                Default: OFF.
              </span>
            </span>
          </label>

          <label class="inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" class="rounded mt-0.5" [(ngModel)]="commerceSharedUi.auto_welcome_email" />
            <span class="text-xs text-gray-800 dark:text-gray-200 leading-relaxed">
              <span class="text-gray-500 dark:text-gray-400 italic">Legacy —</span>
              Cognito temp-password welcome email
              <span class="block mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                When ON, Cognito sends its own temp-password email on every fresh user create.
                Used historically by <code>qpp</code> / <code>delphi-sig</code>. Ignored when the F2-branded
                option above is enabled — only one welcome fires per activate.
                Default: OFF.
              </span>
            </span>
          </label>

          <div class="flex justify-end gap-2">
            <button class="px-3 py-1 rounded border text-xs uppercase tracking-wide"
                    [ngClass]="commerceSharedIsDirty()
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10'
                      : 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'"
                    [disabled]="!commerceSharedIsDirty()"
                    (click)="commerceSharedRevert()"
                    [title]="commerceSharedIsDirty() ? 'Discard unsaved shared-settings changes' : 'No unsaved changes'">
              Revert
            </button>
            <button class="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-xs uppercase tracking-wide"
                    (click)="commerceSharedSave()">
              Save Shared Settings
              <span *ngIf="commerceSharedIsDirty()" class="ml-1 text-amber-200">●</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="px-4 py-3 border-t border-gray-200 dark:border-[#434863] flex justify-end bg-gray-50 dark:bg-app-black-100">
      <button class="px-3 py-1 border border-gray-300 dark:border-[#434863] rounded hover:bg-gray-100 dark:hover:bg-app-blue-100/50 text-gray-900 dark:text-white"
              [ngClass]="commerceHasAnyDirty() ? 'border-amber-500 text-amber-700 dark:text-amber-300' : ''"
              (click)="closeCommerceSettings()">
        Close
        <span *ngIf="commerceHasAnyDirty()" class="ml-1 text-amber-600 dark:text-amber-400">(lose changes)</span>
      </button>
    </div>
  </div>
</div>

